import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { Link, useBlocker, useLocation, useOutletContext, useParams } from "react-router";
import DatePicker from "react-datepicker";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { LuChevronDown, LuChevronsDown, LuChevronsUp, LuPaperclip, LuPlus, LuSearchX, LuTrash2 } from "react-icons/lu";
import ReactJson from "react-json-view";
import ReactDiffViewer from "react-diff-viewer";
import { findAccount, money, costLabel } from "./internalAccounts.js";
import { createPortal } from "react-dom";
import { loadServices, saveServices, TAXONOMY_MISMATCH } from "./serviceCatalog.js";
import InternalTable from "./InternalTable.jsx";
import RuleLines from "./RuleLines.jsx";
import EmrPanel, { EmrTabs } from "./emr/EmrPanel.jsx";
import { emptyForm, formFromService, PickField, serviceFromForm, ServiceFields } from "./ServiceForm.jsx";

const SKIP_SERVICE = new Set(["deductible", "outOfPocketMaximum", "coinsurance", "rx", "excludedServices"]);
const SERVICE_ORDER = ["preventiveCare", "pcpVisit", "specialistVisit", "urgentCare", "emergencyRoom", "inpatientHospital", "outpatientRehabilitation", "chiropractice"];
const SUPPLIES = [
    ["30DaysSupply", "30-day"],
    ["60DaysSupply", "60-day"],
    ["90DaysSupply", "90-day"],
    ["30DaysMailOrder", "30-day mail"],
    ["90DaysMailOrder", "90-day mail"],
    ["specialtyTier", "Specialty"],
];
const TIER_LABEL = { nonPrefered: "Non-preferred", speciality: "Specialty" };
const PROVIDER_NETWORKS = ["Cigna", "Centivo partnership"];
const CENSUS_TIERS = [
    { tierName: "Employee", code: "EEO" },
    { tierName: "Employee + Spouse", code: "EES / EE+1" },
    { tierName: "Employee + Domestic partner", code: "EE+DP" },
    { tierName: "Employee + Child(ren)", code: "EEC" },
    { tierName: "Family", code: "FAM" },
    { tierName: "Family with domestic partner", code: "FAM+DP" },
];
const PBM_VENDORS = ["SmithRX", "Archimedes", "Capital Rx", "FairosRx", "SwyftScripts", "AccessHope", "NYS HCRA"];
const TELEMEDICINE_VENDORS = ["MDLive", "Wheel"];
const ADMIN_FEES = [
    { value: "broker", label: "Broker" },
    { value: "medicalAdmin", label: "Medical admin" },
];
const FEE_BASIS = [
    { value: "pepm", label: "PEPM", description: "Per employee per month. The amount follows each census tier." },
    { value: "pmpm", label: "PMPM", description: "Per member per month. One amount for each covered member." },
    { value: "pepy", label: "PEPY", description: "Per employee per year. The amount follows each census tier." },
    { value: "pmpy", label: "PMPY", description: "Per member per year. One amount for each covered member." },
    { value: "oneTime", label: "One time", description: "A single amount, charged once." },
];
const TIER_BASIS = new Set(["pepm", "pepy"]);
const AMOUNT_BASIS = new Set(["pmpm", "pmpy", "oneTime"]);
const PARTIAL_INTERVAL = [
    { value: "prorated", label: "Prorated", description: "Charge for the days covered in the period." },
    { value: "wash", label: "Wash", description: "Charge the full period, or none of it, when coverage starts or ends mid-period." },
];
const PLACE_OF_SERVICE = [
    ["02", "Telehealth"],
    ["10", "Telehealth, patient home"],
    ["11", "Office"],
    ["12", "Home"],
    ["19", "Off-campus outpatient hospital"],
    ["20", "Urgent care"],
    ["21", "Inpatient hospital"],
    ["22", "On-campus outpatient hospital"],
    ["23", "Emergency room"],
    ["24", "Ambulatory surgical center"],
    ["31", "Skilled nursing"],
    ["41", "Ambulance, land"],
    ["42", "Ambulance, air"],
    ["49", "Independent clinic"],
    ["52", "Psychiatric facility, partial hospital"],
    ["53", "Community mental health center"],
    ["81", "Independent laboratory"],
];
const COVERAGE_MODIFIER = [
    ["95", "Telehealth"],
    ["GT", "Telehealth"],
    ["26", "Professional"],
    ["TC", "Technical"],
    ["33", "Preventive"],
];
const VARIANT_OPTIONS = [
    {
        label: "Place of service",
        options: PLACE_OF_SERVICE.map(([value, label]) => ({ value, label: `${label} (${value})`, kind: "place" })),
    },
    {
        label: "Modifier",
        options: COVERAGE_MODIFIER.map(([value, label]) => ({ value, label: `${value} ${label}`, kind: "modifier" })),
    },
];
const VARIANT_OPTION_LIST = VARIANT_OPTIONS.flatMap((group) => group.options);

const words = (key) => {
    if (TIER_LABEL[key]) return TIER_LABEL[key];
    return String(key || "")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (char) => char.toUpperCase())
        .replace(/\bPcp\b/g, "PCP")
        .replace(/\bHcpcs\b/g, "HCPCS")
        .replace(/\bCpt\b/g, "CPT");
};

const dash = (value) => (value == null || value === "" ? "" : String(value));

const limitText = (limit) => {
    if (!limit) return "";
    const kind = {
        daysPerPlanYear: "days / year",
        dollarsPerPlanYear: "/ year",
        dollarsPerLifetime: "lifetime",
    }[limit.type] || limit.type || "";
    const amount = String(limit.type || "").startsWith("dollars") ? money(limit.value) : limit.value;
    const shared = limit.sharedWith?.length ? ` shared with ${limit.sharedWith.map(words).join(", ")}` : "";
    return `${amount} ${kind}${shared}`.trim();
};

const codeText = (service) => {
    if (!service) return "";
    const bits = [
        ...(service.cptCodes || []),
        ...(service.hcpcsCodes || []),
        ...(service.revenueCode || []),
        ...(service.cptCodeGroups || []).map((group) => `${group.from}–${group.to}`),
        ...(service.hcpcsCodeGroups || []).map((group) => `${group.from}–${group.to}`),
        ...(service.revenueCodeGroups || []).map((group) => `${group.from}–${group.to}`),
    ];
    return bits.join(", ");
};

const noteText = (service) => {
    if (!service) return "";
    return [
        ...(service.conditions || []),
        ...(service.notes || []),
    ].filter(Boolean).join(" · ");
};

const costOf = (service, type) => costLabel((service?.costs || []).find((cost) => cost.type === type));

const serviceKeys = (plan) => {
    const keys = new Set();
    [plan.inNetwork, plan.outOfNetwork].forEach((side) => {
        Object.keys(side || {}).forEach((key) => {
            if (!SKIP_SERVICE.has(key)) keys.add(key);
        });
    });
    return [...keys].sort((a, b) => {
        const left = SERVICE_ORDER.indexOf(a);
        const right = SERVICE_ORDER.indexOf(b);
        if (left === -1 && right === -1) return a.localeCompare(b);
        if (left === -1) return 1;
        if (right === -1) return -1;
        return left - right;
    });
};

const parseDate = (value) => {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatDate = (date) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
};

const numberValue = (value) => {
    if (value === "" || value == null) return null;
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
};

const DateField = ({ label, name }) => {
    const canEdit = useCanEdit();
    const { control } = useFormContext();
    return (
        <div className="internal-work__field">
            <span>{label}</span>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <DatePicker
                        selected={parseDate(field.value)}
                        onChange={(date) => field.onChange(formatDate(date))}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="yyyy-mm-dd"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        isClearable={canEdit}
                        disabled={!canEdit}
                    />
                )}
            />
        </div>
    );
};

const FieldHint = ({ text }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    return (
        <span className="internal-work__hint" ref={rootRef}>
            <button type="button" aria-label="About this field" aria-expanded={open} onClick={() => setOpen((value) => !value)}>!</button>
            {open ? <span className="internal-work__hint-pop" role="note">{text}</span> : null}
        </span>
    );
};

const Field = ({ label, name, value, wide, numeric, hint }) => {
    const canEdit = useCanEdit();
    const { register } = useFormContext();
    return (
        <div className={`internal-work__field${wide ? " is-wide" : ""}`}>
            <span>{label}{hint ? <FieldHint text={hint} /> : null}</span>
            {name ? (
                <input type="text" readOnly={!canEdit} {...register(name, numeric ? { setValueAs: numberValue } : undefined)} />
            ) : (
                <input type="text" readOnly value={dash(value)} />
            )}
        </div>
    );
};

const Check = ({ label, name, checked }) => {
    const canEdit = useCanEdit();
    const { control } = useFormContext();
    return (
        <label className="internal-work__check">
            {name ? (
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                        <input
                            type="checkbox"
                            checked={field.value === true || field.value === "Included"}
                            onChange={(event) => {
                                if (canEdit) field.onChange(event.target.checked);
                            }}
                        />
                    )}
                />
            ) : (
                <input type="checkbox" checked={Boolean(checked)} readOnly onChange={() => {}} />
            )}
            {label}
        </label>
    );
};

const emptyNote = () => ({ note: "", attachments: [] });

const NoteAttachments = ({ name }) => {
    const canEdit = useCanEdit();
    const inputRef = useRef(null);
    const { control, getValues, setValue } = useFormContext();
    const attachments = useWatch({ control, name }) || [];
    const attach = (event) => {
        const files = [...event.target.files];
        const next = [
            ...(getValues(name) || []),
            ...files.map((file) => ({ name: file.name, s3Path: "" })),
        ];
        setValue(name, next, { shouldDirty: true });
        event.target.value = "";
    };
    const remove = (index) => {
        const next = [...(getValues(name) || [])];
        next.splice(index, 1);
        setValue(name, next, { shouldDirty: true });
    };
    if (!canEdit && attachments.length === 0) return null;
    return (
        <div className="internal-work__documents">
            {attachments.map((document, index) => (
                <div className="internal-work__document" key={`${document.name}-${index}`}>
                    <LuPaperclip />
                    <span>{document.name}</span>
                    {document.s3Path ? <span className="internal-work__document-path">{document.s3Path}</span> : null}
                    <IconButton label={`Remove ${document.name || "attachment"}`} danger onClick={() => remove(index)}>
                        <LuTrash2 />
                    </IconButton>
                </div>
            ))}
            {canEdit && (
                <>
                    <input ref={inputRef} type="file" multiple hidden onChange={attach} />
                    <button type="button" className="internal-work__attach" onClick={() => inputRef.current?.click()}>
                        <LuPaperclip />
                        Attach
                    </button>
                </>
            )}
        </div>
    );
};

const Notes = ({ name }) => {
    const canEdit = useCanEdit();
    const { register, control, getValues, setValue } = useFormContext();
    const watched = useWatch({ control, name });
    const notes = Array.isArray(watched) ? watched : [];
    const remove = (index) => {
        const next = [...(getValues(name) || [])];
        next.splice(index, 1);
        setValue(name, next, { shouldDirty: true });
    };
    return (
        <div className="internal-work__notes-list">
            {notes.map((item, index) => (
                <div className="internal-work__note" key={`${name}-${index}`}>
                    <div className="internal-work__item">
                        <div>
                            <textarea
                                className="internal-work__notes"
                                rows={4}
                                maxLength={500}
                                readOnly={!canEdit}
                                aria-label="Note"
                                {...register(`${name}.${index}.note`)}
                            />
                            <p>{String(item?.note || "").length} / 500</p>
                            <NoteAttachments name={`${name}.${index}.attachments`} />
                        </div>
                        <IconButton label="Remove note" danger onClick={() => remove(index)}>
                            <LuTrash2 />
                        </IconButton>
                    </div>
                </div>
            ))}
            {notes.length === 0 && <p>No notes yet.</p>}
        </div>
    );
};

const Checks = ({ children }) => <div className="internal-work__checks">{children}</div>;

const AddressFields = ({ name }) => (
    <div className="internal-work__address">
        <Field label="Attention" name={`${name}.attention`} wide />
        <div className="internal-work__streets">
            <Field label="Street 1" name={`${name}.street1`} />
            <Field label="Street 2" name={`${name}.street2`} />
        </div>
        <Field label="City" name={`${name}.city`} />
        <Field label="State" name={`${name}.state`} />
        <Field label="ZIP" name={`${name}.zip`} />
    </div>
);

const PurposeSelect = ({ name }) => {
    const canEdit = useCanEdit();
    const { control } = useFormContext();
    return (
        <div className="internal-work__field">
            <span>Purpose</span>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!canEdit}
                        isSearchable={false}
                        options={BANK_PURPOSES}
                        value={BANK_PURPOSES.find((option) => option.value === field.value) || null}
                        onChange={(option) => field.onChange(option ? option.value : "")}
                        placeholder="Claims funding or monthly bills"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={formSelectStyles}
                    />
                )}
            />
        </div>
    );
};

const BankFields = ({ name }) => (
    <div className="internal-work__fields">
        <PurposeSelect name={`${name}.purpose`} />
        <Field label="Bank name" name={`${name}.bankName`} />
        <Field label="Routing number" name={`${name}.routingNumber`} />
        <Field label="Account number" name={`${name}.accountNumber`} />
    </div>
);

const BankList = ({ banks, name, onAdd, onRemove }) => (
    <SubForm title="Bank accounts" onAdd={onAdd}>
        {(banks || []).map((bank, index) => (
            <SubForm
                title={bankPurposeLabel(bank.purpose)}
                key={`${bank.purpose || "bank"}-${index}`}
                onRemove={() => onRemove(index)}
            >
                <BankFields name={`${name}.${index}`} />
            </SubForm>
        ))}
        {(banks || []).length === 0 && <p>No bank account yet.</p>}
    </SubForm>
);

const QuietInput = ({ name, label, numeric }) => {
    const canEdit = useCanEdit();
    const { register } = useFormContext();
    return (
        <input
            type="text"
            aria-label={label}
            readOnly={!canEdit}
            {...register(name, numeric ? { setValueAs: numberValue } : undefined)}
        />
    );
};

const Amounts = ({ name, side }) => (
    <div className="internal-work__matrix">
        <span />
        <span>Individual</span>
        <span>Family</span>
        <span>Deductible</span>
        <QuietInput label="Deductible, individual" name={`${name}.deductible.individual`} numeric />
        <QuietInput label="Deductible, family" name={`${name}.deductible.family`} numeric />
        <span>Out-of-pocket</span>
        <QuietInput label="Out-of-pocket, individual" name={`${name}.outOfPocketMaximum.individual`} numeric />
        <QuietInput label="Out-of-pocket, family" name={`${name}.outOfPocketMaximum.family`} numeric />
        <span className="is-hint">
            Member coinsurance
            <FieldHint text="The percent a member pays after the deductible. One number covers the whole network, so it sits here with the deductible and out-of-pocket amounts." />
        </span>
        <input type="text" readOnly value={costLabel(side?.coinsurance)} aria-label="Member coinsurance" />
    </div>
);

const FoldContext = createContext(null);
const EditModeContext = createContext(false);
const useCanEdit = () => useContext(EditModeContext);

const useFold = () => {
    const fold = useContext(FoldContext);
    const [open, setOpen] = useState(true);
    const seen = useRef(fold?.tick || 0);
    useEffect(() => {
        if (!fold || fold.tick === seen.current) return;
        seen.current = fold.tick;
        setOpen(fold.open);
    }, [fold]);
    return [open, setOpen];
};

const IconButton = ({ label, onClick, danger, children }) => {
    const canEdit = useCanEdit();
    if (!canEdit) return null;
    return (
        <button type="button" className={`internal-work__icon${danger ? " is-danger" : ""}`} aria-label={label} onClick={onClick}>
            {children}
        </button>
    );
};

const SubForm = ({ title, children, onAdd, onRemove }) => {
    const [open, setOpen] = useFold();
    return (
        <div className={`internal-work__group${open ? "" : " is-collapsed"}`}>
            <div className="internal-work__subline">
                <button type="button" className="internal-work__subcollapse" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                    <h3>{title}</h3>
                </button>
                {(onAdd || onRemove) && (
                    <span className="internal-work__item-actions">
                        {onAdd ? <IconButton label={`Add to ${title}`} onClick={onAdd}><LuPlus /></IconButton> : null}
                        {onRemove ? <IconButton label={`Remove ${title}`} onClick={onRemove} danger><LuTrash2 /></IconButton> : null}
                    </span>
                )}
            </div>
            <div className="internal-work__fold-body">{children}</div>
        </div>
    );
};

const emptyContact = () => ({ name: "", title: "", phone: "", email: "" });
const emptyAddress = () => ({ type: "mailing", attention: "", street1: "", street2: "", city: "", state: "", zip: "" });
const BANK_PURPOSES = [
    { value: "claimsFunding", label: "Claims funding" },
    { value: "monthlyBills", label: "Monthly bills" },
];
const bankPurposeLabel = (purpose) => BANK_PURPOSES.find((item) => item.value === purpose)?.label || "Bank account";
const emptyBank = (banks) => {
    const used = new Set((banks || []).map((bank) => bank.purpose));
    const purpose = used.has("claimsFunding") ? "monthlyBills" : "claimsFunding";
    return { purpose, bankName: "", routingNumber: "", accountNumber: "" };
};
const emptySubAccount = () => ({
    accountName: "New sub-account",
    contacts: [],
    address: [],
    bankAccounts: [],
    billingPreference: { billTopAccount: false },
    benefitPlans: [],
});

const emptyPreventiveCare = () => {
    const catalog = loadServices().find((service) => service.key === "preventiveCare");
    const service = {
        isCovered: true,
        costs: [{ type: "copayAmount", value: 0 }],
        deductibleApplies: false,
    };
    if (catalog) applyCatalogCodes(service, catalog);
    return service;
};

const emptyPlan = (planName) => ({
    planName,
    planId: "",
    tiers: [],
    networks: [],
    telemedicine: [],
    pbm: [],
    fees: [],
    inNetwork: { preventiveCare: emptyPreventiveCare() },
    outOfNetwork: {},
    coverage: [],
    rules: [],
    notes: [],
});

const ContactFields = ({ name, onRemove }) => (
    <div className="internal-work__item">
        <div className="internal-work__fields">
            <Field label="Name" name={`${name}.name`} />
            <Field label="Title" name={`${name}.title`} />
            <Field label="Phone" name={`${name}.phone`} />
            <Field label="Email" name={`${name}.email`} />
        </div>
        <IconButton label="Remove contact" onClick={onRemove} danger><LuTrash2 /></IconButton>
    </div>
);

const FormSection = ({ title, description, children }) => {
    const [open, setOpen] = useFold();
    return (
        <section className={`internal-work__form-section${open ? "" : " is-collapsed"}`}>
            <div className="internal-work__form-aside">
                <button type="button" className="internal-work__collapse" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                    <h2>{title}</h2>
                </button>
                {description ? <p>{description}</p> : null}
            </div>
            <div className="internal-work__form-body">{children}</div>
        </section>
    );
};

const PlanSelect = ({ name, plans }) => {
    const canEdit = useCanEdit();
    const { control } = useFormContext();
    const options = (plans || []).map((plan) => ({
        value: plan.planId || plan.planName,
        label: plan.planName,
        planId: plan.planId,
        planName: plan.planName,
    }));
    return (
        <div className="internal-work__field is-wide">
            <span>Plans</span>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isMulti
                        isDisabled={!canEdit}
                        options={options}
                        value={options.filter((option) => (
                            (field.value || []).some((plan) => plan.planId === option.planId || plan.planName === option.planName)
                        ))}
                        onChange={(next) => field.onChange((next || []).map((option) => ({ planName: option.planName, planId: option.planId })))}
                        placeholder="Select plans"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={formSelectStyles}
                    />
                )}
            />
        </div>
    );
};

const ChoiceField = ({ label, name, options, placeholder }) => {
    const canEdit = useCanEdit();
    const { control } = useFormContext();
    return (
        <div className="internal-work__field">
            <span>{label}</span>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!canEdit}
                        isSearchable={false}
                        options={options}
                        value={options.find((option) => option.value === field.value) || null}
                        onChange={(option) => field.onChange(option ? option.value : "")}
                        formatOptionLabel={(option, { context }) => (
                            context === "menu" && option.description ? (
                                <span className="internal-work__menu-option">
                                    <span>{option.label}</span>
                                    <small>{option.description}</small>
                                </span>
                            ) : option.label
                        )}
                        placeholder={placeholder || ""}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={{
                            ...formSelectStyles,
                            menu: (base) => ({
                                ...formSelectStyles.menu(base),
                                minWidth: options.some((option) => option.description) ? "18rem" : undefined,
                            }),
                            option: (base, state) => ({
                                ...formSelectStyles.option(base, state),
                                whiteSpace: "normal",
                            }),
                        }}
                    />
                )}
            />
        </div>
    );
};

const emptyCharge = () => ({ basis: "pepm", amount: null, tiers: [] });
const emptyVendor = () => ({ vendor: "", ...emptyCharge() });
const emptyNetwork = () => ({ name: "", ...emptyCharge() });
const emptyAdminFee = () => ({ party: "broker", ...emptyCharge() });

const rateLabel = (tier) => {
    const known = {
        EEO: "Employee",
        "EES / EE+1": "EE + Spouse",
        "EE+DP": "EE + Domestic partner",
        EEC: "EE + Child(ren)",
        FAM: "Family",
        "FAM+DP": "Family + DP",
    };
    return known[tier.code] || tier.tierName || "Tier";
};

const FeeTierAmounts = ({ plan, item, editPlan, listKey, index }) => {
    const canEdit = useCanEdit();
    const tiers = plan.tiers || [];
    const basisLabel = FEE_BASIS.find((option) => option.value === item.basis)?.label || "PEPM";
    if (!tiers.length) return <p>Add census tiers to enter {basisLabel} amounts.</p>;
    const writeAmount = (tier, raw) => {
        const cleaned = String(raw || "").replace(/[$,\s]/g, "");
        const amount = cleaned === "" ? null : Number(cleaned);
        if (cleaned !== "" && Number.isNaN(amount)) return;
        editPlan((target) => {
            const row = target[listKey]?.[index];
            if (!row) return;
            row.tiers = row.tiers || [];
            let entry = row.tiers.find((line) => line.code === tier.code || line.tierName === tier.tierName);
            if (!entry) {
                entry = { tierName: tier.tierName, code: tier.code };
                row.tiers.push(entry);
            }
            entry.amount = amount;
        });
    };
    return (
        <div className="internal-work__rate" role="group" aria-label={`Census ${basisLabel}`}>
            {tiers.map((tier) => {
                const entry = (item.tiers || []).find((line) => line.code === tier.code || line.tierName === tier.tierName);
                const shown = entry?.amount == null ? "" : String(entry.amount);
                return (
                    <label className="internal-work__rate-cell" key={`${listKey}-${index}-${tier.code || tier.tierName}`}>
                        <span>{rateLabel(tier)}</span>
                        <small>{tier.code}</small>
                        <span className="internal-work__rate-money">
                            <i>$</i>
                            <input
                                type="text"
                                inputMode="decimal"
                                readOnly={!canEdit}
                                aria-label={`${rateLabel(tier)} ${basisLabel}`}
                                defaultValue={shown}
                                key={shown}
                                onBlur={(event) => writeAmount(tier, event.target.value)}
                            />
                        </span>
                    </label>
                );
            })}
        </div>
    );
};

const FeeSchedule = ({ name, item, plan, editPlan, listKey, index, who }) => (
    <>
        <div className="internal-work__fields">
            {who}
            <ChoiceField label="Basis" name={`${name}.basis`} options={FEE_BASIS} />
            {item.basis !== "oneTime" ? (
                <ChoiceField label="Partial interval" name={`${name}.partialInterval`} options={PARTIAL_INTERVAL} placeholder="Prorated or wash" />
            ) : null}
            {AMOUNT_BASIS.has(item.basis) ? <Field label="Amount" name={`${name}.amount`} numeric /> : null}
        </div>
        {TIER_BASIS.has(item.basis) ? <FeeTierAmounts plan={plan} item={item} editPlan={editPlan} listKey={listKey} index={index} /> : null}
    </>
);

const choiceOptions = (values) => values.map((value) => ({ value, label: value }));

const AccountForm = ({ account, edit }) => (
    <FormSection title="Details" description="Account name and ID, sub-accounts, contacts, addresses, and bank accounts.">
        <div className="internal-work__fields">
            <Field label="Account name" name="accountName" />
            <Field label="Account ID" name="id" />
            <div className="internal-work__dates">
                <DateField label="Effective date" name="effectiveDate" />
                <DateField label="Termination date" name="terminationDate" />
            </div>
        </div>
        <SubForm title="Sub-accounts" onAdd={() => edit((next) => { next.subAccounts = [...(next.subAccounts || []), emptySubAccount()]; })}>
            {(account.subAccounts || []).map((sub, index) => (
                <SubForm
                    title={sub.accountName}
                    key={`${sub.accountName}-${index}`}
                    onRemove={() => edit((next) => { next.subAccounts.splice(index, 1); })}
                >
                    <Checks>
                        <Check label="Bill the top account" name={`subAccounts.${index}.billingPreference.billTopAccount`} />
                    </Checks>
                    <div className="internal-work__fields">
                        <Field label="Account name" name={`subAccounts.${index}.accountName`} />
                        <PlanSelect name={`subAccounts.${index}.benefitPlans`} plans={account.benefitPlans} />
                        <div className="internal-work__dates">
                            <DateField label="Effective date" name={`subAccounts.${index}.effectiveDate`} />
                            <DateField label="Termination date" name={`subAccounts.${index}.terminationDate`} />
                        </div>
                    </div>
                    <SubForm title="Contacts" onAdd={() => edit((next) => { next.subAccounts[index].contacts = [...(next.subAccounts[index].contacts || []), emptyContact()]; })}>
                        {(sub.contacts || []).map((contact, contactIndex) => (
                            <ContactFields
                                name={`subAccounts.${index}.contacts.${contactIndex}`}
                                key={`${contact.email || contact.name}-${contactIndex}`}
                                onRemove={() => edit((next) => { next.subAccounts[index].contacts.splice(contactIndex, 1); })}
                            />
                        ))}
                        {(sub.contacts || []).length === 0 && <p>No contact yet.</p>}
                    </SubForm>
                    <SubForm title="Addresses" onAdd={() => edit((next) => { next.subAccounts[index].address = [...(next.subAccounts[index].address || []), emptyAddress()]; })}>
                        {(sub.address || []).map((address, addressIndex) => (
                            <SubForm
                                title={words(address.type || "Address")}
                                key={`${address.type}-${address.street1}-${addressIndex}`}
                                onRemove={() => edit((next) => { next.subAccounts[index].address.splice(addressIndex, 1); })}
                            >
                                <AddressFields name={`subAccounts.${index}.address.${addressIndex}`} />
                            </SubForm>
                        ))}
                        {(sub.address || []).length === 0 && <p>No address yet.</p>}
                    </SubForm>
                    <BankList
                        banks={sub.bankAccounts}
                        name={`subAccounts.${index}.bankAccounts`}
                        onAdd={() => edit((next) => {
                            const banks = next.subAccounts[index].bankAccounts || [];
                            next.subAccounts[index].bankAccounts = [...banks, emptyBank(banks)];
                        })}
                        onRemove={(bankIndex) => edit((next) => { next.subAccounts[index].bankAccounts.splice(bankIndex, 1); })}
                    />
                </SubForm>
            ))}
            {(account.subAccounts || []).length === 0 && <p>No sub-accounts.</p>}
        </SubForm>
        <SubForm title="Contacts" onAdd={() => edit((next) => { next.contacts = [...(next.contacts || []), emptyContact()]; })}>
            {(account.contacts || []).map((contact, index) => (
                <ContactFields
                    name={`contacts.${index}`}
                    key={`${contact.email || contact.name}-${index}`}
                    onRemove={() => edit((next) => { next.contacts.splice(index, 1); })}
                />
            ))}
            {(account.contacts || []).length === 0 && <p>No contact yet.</p>}
        </SubForm>
        <SubForm title="Addresses" onAdd={() => edit((next) => { next.address = [...(next.address || []), emptyAddress()]; })}>
            {(account.address || []).map((address, index) => (
                <SubForm
                    title={words(address.type || "Address")}
                    key={`${address.type}-${address.street1}-${index}`}
                    onRemove={() => edit((next) => { next.address.splice(index, 1); })}
                >
                    <AddressFields name={`address.${index}`} />
                </SubForm>
            ))}
            {(account.address || []).length === 0 && <p>No address yet.</p>}
        </SubForm>
        <BankList
            banks={account.bankAccounts}
            name="bankAccounts"
            onAdd={() => edit((next) => { next.bankAccounts = [...(next.bankAccounts || []), emptyBank(next.bankAccounts)]; })}
            onRemove={(index) => edit((next) => { next.bankAccounts.splice(index, 1); })}
        />
        <SubForm title="Notes" onAdd={() => edit((next) => { next.notes = [...(next.notes || []), emptyNote()]; })}>
            <Notes name="notes" />
        </SubForm>
    </FormSection>
);

const bothSides = (left, right) => {
    if (!left) return right;
    if (!right || left === right) return left;
    return `In network: ${left} · Out of network: ${right}`;
};

const cellText = (value) => dash(value);
const cellCheck = (checked) => (checked ? "Yes" : "No");

const sideCells = (service) => (
    service
        ? [cellCheck(service.isCovered), cellText(costOf(service, "copayAmount")), cellText(costOf(service, "copayPercent")), cellCheck(service.deductibleApplies)]
        : ["", "", "", ""]
);

const penaltyAmount = (penalty) => (
    penalty?.value != null && penalty.valueType !== "percent" ? money(penalty.value) : ""
);

const penaltyPercent = (penalty) => (
    penalty?.value != null && penalty.valueType === "percent" ? `${penalty.value}%` : ""
);

const penaltyCells = (penalty) => ["", penaltyAmount(penalty), penaltyPercent(penalty), ""];

const precertCells = (precert) => (
    precert
        ? [
            precert.required == null ? "" : cellCheck(precert.required),
            penaltyAmount(precert.penalty),
            penaltyPercent(precert.penalty),
            "",
        ]
        : ["", "", "", ""]
);

const responsibilityLabel = (precert) => {
    if (precert?.responsibility === "provider") return "Provider";
    if (precert?.responsibility === "member") return "Member";
    return "";
};

const visitCells = (share) => (
    share
        ? [
            share.isCovered == null ? "" : cellCheck(share.isCovered),
            cellText(costOf(share, "copayAmount")),
            cellText(costOf(share, "copayPercent")),
            share.deductibleApplies == null ? "" : cellCheck(share.deductibleApplies),
        ]
        : ["", "", "", ""]
);

const namedCode = (list, code) => list.find(([value]) => value === code)?.[1] || "";

const variantLabel = (variant) => {
    if (!variant) return "";
    const places = (variant.placesOfService || []).map((code) => {
        const name = namedCode(PLACE_OF_SERVICE, code);
        return name ? `${name} (${code})` : code;
    });
    const modifiers = (variant.modifiers || []).map((code) => {
        const name = namedCode(COVERAGE_MODIFIER, code);
        return name ? `${code} ${name}` : code;
    });
    return [...places, ...modifiers].join(", ");
};

const variantIdentity = (left, right) => {
    const inn = variantLabel(left);
    const oon = variantLabel(right);
    if (inn && oon && inn !== oon) return `In network: ${inn} · Out of network: ${oon}`;
    return inn || oon;
};

const variantShown = (row) => (variantLabel(row.inn) ? row.inn : (row.oon || row.inn || {}));

const withVariants = (row) => {
    const count = Math.max(row.inn?.variants?.length || 0, row.oon?.variants?.length || 0);
    const variants = Array.from({ length: count }, (_, variantIndex) => {
        const inn = row.inn?.variants?.[variantIndex] || null;
        const oon = row.oon?.variants?.[variantIndex] || null;
        return {
            kind: "variant",
            parentKind: row.kind,
            parentKey: row.key,
            coverageIndex: row.index,
            variantIndex,
            label: variantIdentity(inn, oon) || "Place of service",
            inn,
            oon,
        };
    });
    return [row, ...variants];
};

const penaltyRow = (row, penaltyKey, label) => {
    if (row.kind !== "service") return [];
    return [{
        kind: "penalty",
        penaltyKey,
        parentKind: row.kind,
        parentKey: row.key,
        label,
        inn: row.inn?.[penaltyKey] || null,
        oon: row.oon?.[penaltyKey] || null,
    }];
};

const VISIT_LINES = {
    basiclabs: [
        ["withPcpVisit", "With PCP visit"],
        ["withObgynVisit", "With OB/GYN visit"],
        ["withSpecialistVisit", "With specialist visit"],
    ],
    "basicDiagnostic/radiology": [
        ["withPcpVisit", "With PCP visit"],
        ["withSpecialistVisit", "With specialist visit"],
    ],
    basicLabImaging: [
        ["withPcpVisit", "With PCP visit"],
        ["withSpecialistVisit", "With specialist visit"],
    ],
};

const renderedByLabel = (row) => {
    const rendered = row.inn?.renderedByMemberPcp ?? row.oon?.renderedByMemberPcp;
    if (rendered == null) return "";
    return rendered ? "Yes" : "No";
};

const ER_SHARES = [
    ["nonEmergentFacility", "Non-emergent facility"],
    ["professional", "Emergent professional"],
    ["professionalNonEmergent", "Non-emergent professional"],
];

const shareRows = (row, lines) => lines.map(([field, label]) => ({
    kind: "visit",
    field,
    memberPcp: false,
    parentKind: row.kind,
    parentKey: row.key,
    label,
    inn: row.inn?.[field] || null,
    oon: row.oon?.[field] || null,
}));

const erShareRows = (row) => (
    row.kind === "service" && row.key === "emergencyRoom" ? shareRows(row, ER_SHARES) : []
);

const maxDaysRow = (row) => {
    if (row.kind !== "service" || row.key !== "emergencyRoom") return [];
    const days = row.inn?.maxNumberOfDaysCovered ?? row.oon?.maxNumberOfDaysCovered;
    return [{
        kind: "maxDays",
        parentKind: row.kind,
        parentKey: row.key,
        label: "Max days",
        days: days == null ? null : days,
    }];
};

const conditionPenaltyRow = (row) => {
    if (row.kind !== "service") return [];
    const hasPenalty = row.inn?.penalty || row.oon?.penalty;
    if (row.key !== "inpatientHospital" && !hasPenalty) return [];
    return penaltyRow(row, "penalty", "Penalty").map((line) => ({ ...line, condition: true }));
};

const visitRows = (row) => (VISIT_LINES[row.kind === "service" ? row.key : ""] || []).map(([field, label]) => ({
    kind: "visit",
    field,
    memberPcp: field === "withPcpVisit",
    parentKind: row.kind,
    parentKey: row.key,
    label,
    inn: row.inn?.[field] || null,
    oon: row.oon?.[field] || null,
}));

const PRECERT_SERVICES = new Set(["inpatientHospital", "outpatientSurgery", "advanceDiagnostic"]);

const precertRows = (row) => {
    if (row.kind !== "service" || !PRECERT_SERVICES.has(row.key)) return [];
    return [{
        kind: "precert",
        parentKind: row.kind,
        parentKey: row.key,
        label: "Precertification",
        inn: row.inn?.precertification || null,
        oon: row.oon?.precertification || null,
    }];
};

const sharedField = (row, field) => {
    if (row.inn?.[field] != null && row.inn[field] !== "") return row.inn[field];
    return row.oon?.[field];
};

const sharedRules = (row) => {
    if (row.inn?.rules?.length) return row.inn.rules;
    if (row.oon?.rules?.length) return row.oon.rules;
    return null;
};

const patchServiceSides = (target, row, recipe) => {
    const sides = ["inn", "oon"].map((side) => readSide(target, row, side)).filter(Boolean);
    const unique = [...new Set(sides)];
    const services = unique.length ? unique : [ensureSide(target, row, "inn")].filter(Boolean);
    services.forEach(recipe);
};

const catalogEntry = (row) => (
    row.parentKind === "service" ? loadServices().find((service) => service.key === row.parentKey) || null : null
);

const mismatchLabel = (value) => TAXONOMY_MISMATCH.find((option) => option.value === value)?.label || "";

const AdjudicationPanel = ({ row, editPlan, onOpenForm }) => {
    const canEdit = useCanEdit();
    const parent = { kind: row.parentKind, key: row.parentKey, index: row.coverageIndex };
    const catalog = catalogEntry(row);
    const planRules = sharedRules(row);
    const planDescription = sharedField(row, "humanDescription");
    const custom = Boolean(planRules || planDescription);
    const rules = planRules || catalog?.rules || [];
    const description = planDescription ?? catalog?.humanDescription ?? "";
    const mismatch = sharedField(row, "taxonomyMismatch") || "";
    const maxUnits = sharedField(row, "maxDailyUnits");
    const taxonomies = catalog?.taxonomyCodes || [];
    const patch = (recipe) => editPlan((target) => patchServiceSides(target, parent, recipe));
    const setRules = (next) => patch((service) => {
        const lines = next.filter((rule) => rule.when || rule.then);
        if (lines.length) service.rules = lines;
        else delete service.rules;
    });
    const customize = () => patch((service) => {
        service.rules = structuredClone(catalog?.rules || []);
        if (catalog?.humanDescription) service.humanDescription = catalog.humanDescription;
    });
    const useCatalog = () => patch((service) => {
        delete service.rules;
        delete service.humanDescription;
    });
    const oopDefault = catalog?.countsTowardOutOfPocket;
    const yesNo = (value) => {
        if (value == null) return "";
        return value ? "Yes" : "No";
    };
    return (
        <div className="internal-work__adjudication">
            <div className="internal-work__fields is-quad">
                {["inn", "oon"].map((side) => {
                    const service = side === "inn" ? row.inn : row.oon;
                    return (
                        <div className="internal-work__field" key={side}>
                            <span>{side === "inn" ? "INN counts toward out-of-pocket" : "OON counts toward out-of-pocket"}</span>
                            <EditableCell
                                kind="bool"
                                value={yesNo(service?.countsTowardOutOfPocket)}
                                placeholder={oopDefault == null ? "" : `${yesNo(oopDefault)} (catalog)`}
                                onCommit={(next) => editPlan((target) => {
                                    const current = next === "" ? readSide(target, parent, side) : ensureSide(target, parent, side);
                                    if (!current) return;
                                    if (next === "") delete current.countsTowardOutOfPocket;
                                    else current.countsTowardOutOfPocket = next === "Yes";
                                })}
                            />
                        </div>
                    );
                })}
                <div className="internal-work__field">
                    <span>Max units per day</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        readOnly={!canEdit}
                        aria-label="Max units per day"
                        placeholder={catalog?.maxDailyUnits == null ? "" : `${catalog.maxDailyUnits} (catalog)`}
                        defaultValue={maxUnits == null ? "" : String(maxUnits)}
                        key={maxUnits == null ? "" : String(maxUnits)}
                        onBlur={(event) => {
                            const cleaned = event.target.value.replace(/[^\d.]/g, "");
                            const units = cleaned === "" || Number.isNaN(Number(cleaned)) ? null : Number(cleaned);
                            patch((service) => {
                                if (units == null) delete service.maxDailyUnits;
                                else service.maxDailyUnits = units;
                            });
                        }}
                    />
                </div>
                <div className="internal-work__field">
                    <span>
                        When the provider type doesn't match
                        <FieldHint text="Each service in the catalog lists the provider types that can bill it, such as family doctors for a PCP visit. Pick what this plan does when a different type of provider sends the bill." />
                    </span>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!canEdit}
                        isClearable
                        isSearchable={false}
                        options={TAXONOMY_MISMATCH}
                        value={TAXONOMY_MISMATCH.find((option) => option.value === mismatch) || null}
                        onChange={(option) => patch((service) => {
                            if (option?.value) service.taxonomyMismatch = option.value;
                            else delete service.taxonomyMismatch;
                        })}
                        formatOptionLabel={(option, { context }) => (
                            context === "menu" ? (
                                <span className="internal-work__menu-option">
                                    <span>{option.label}</span>
                                    <small>{option.description}</small>
                                </span>
                            ) : option.label
                        )}
                        placeholder={catalog?.taxonomyMismatch ? `${mismatchLabel(catalog.taxonomyMismatch)} (catalog)` : "Choose what happens"}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={{ ...formSelectStyles, menu: (base) => ({ ...formSelectStyles.menu(base), minWidth: "20rem" }) }}
                    />
                </div>
            </div>
            <p className="internal-work__adj-codes">
                {taxonomies.length ? `Provider types (taxonomy): ${taxonomies.join(", ")}` : "Any provider type."}
                {catalog?.placesOfService?.length ? ` · Place of service: ${catalog.placesOfService.join(", ")}` : ""}
                {catalog?.typeOfBill?.length ? ` · Type of bill: ${catalog.typeOfBill.join(", ")}` : ""}
            </p>
            <div className="internal-work__adj-head">
                <span>{custom ? "Rules for this plan" : catalog?.rules?.length ? "Rules from the service catalog" : "Rules"}</span>
                {canEdit && catalog && !custom && catalog.rules?.length ? (
                    <button type="button" onClick={customize}>Customize for this plan</button>
                ) : null}
                {canEdit && catalog && custom ? (
                    <button type="button" onClick={useCatalog}>Use the catalog</button>
                ) : null}
                {canEdit && onOpenForm && row.parentKind === "service" ? (
                    <button type="button" onClick={() => onOpenForm(row.parentKey)}>Open in form</button>
                ) : null}
            </div>
            <RuleLines
                key={custom ? "plan" : "catalog"}
                rules={rules}
                readOnly={!canEdit || (!custom && Boolean(catalog?.rules?.length))}
                onChange={setRules}
            />
            <div className="internal-work__field">
                <span>In plain language</span>
                <textarea
                    className="internal-work__notes"
                    readOnly={!canEdit || (!custom && Boolean(catalog?.humanDescription))}
                    aria-label="In plain language"
                    defaultValue={description}
                    key={`${custom}-${description}`}
                    onBlur={(event) => {
                        const text = event.target.value.trim();
                        if (text === description) return;
                        patch((service) => {
                            if (text) service.humanDescription = text;
                            else delete service.humanDescription;
                        });
                    }}
                />
            </div>
        </div>
    );
};

const LeaveConfirm = ({ blocker }) => {
    const stayRef = useRef(null);
    useEffect(() => {
        if (blocker.state !== "blocked") return undefined;
        stayRef.current?.focus();
        const onKey = (event) => {
            if (event.key === "Escape") blocker.reset();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [blocker]);
    if (blocker.state !== "blocked") return null;
    return createPortal(
        <div className="internal-work__confirm-backdrop">
            <div className="internal-work internal-work__confirm" role="alertdialog" aria-modal="true" aria-labelledby="leave-title" aria-describedby="leave-text">
                <h2 id="leave-title">Leave without submitting?</h2>
                <p id="leave-text">You have changes on this account that you haven't submitted. If you leave now, those changes will be lost.</p>
                <div className="internal-work__form-actions">
                    <button type="button" className="internal-work__reset" onClick={() => blocker.proceed()}>Leave and lose changes</button>
                    <button type="button" className="internal-work__submit" ref={stayRef} onClick={() => blocker.reset()}>Stay on this page</button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

const COST_SLOTS = ["Covered", "Copay", "Coinsurance %", "Deductible applies"];
const YES_NO_OPTIONS = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
];
const PLAN_ADJUDICATION_FIELDS = ["maxDailyUnits", "countsTowardOutOfPocket", "taxonomyMismatch"];
const NEW_SERVICE = "__new__";

const planServiceForm = (plan, key, catalog) => {
    const inn = plan.inNetwork?.[key];
    const oon = plan.outOfNetwork?.[key];
    const side = inn || oon || {};
    const base = catalog ? formFromService(catalog) : { ...emptyForm(), name: words(key) };
    const hasCodes = CODE_FIELDS.some((field) => side[field]?.length);
    const codes = hasCodes ? formFromService({ key, ...side }) : base;
    const pick = (field) => (side[field] ?? catalog?.[field]);
    const adjudication = formFromService({
        key,
        maxDailyUnits: pick("maxDailyUnits"),
        countsTowardOutOfPocket: pick("countsTowardOutOfPocket"),
        taxonomyMismatch: pick("taxonomyMismatch"),
        rules: side.rules?.length ? side.rules : catalog?.rules,
        humanDescription: side.humanDescription ?? catalog?.humanDescription,
    });
    return {
        ...base,
        name: words(key),
        cptCodes: codes.cptCodes,
        cptRanges: codes.cptRanges,
        hcpcsCodes: codes.hcpcsCodes,
        hcpcsRanges: codes.hcpcsRanges,
        revenueCodes: codes.revenueCodes,
        revenueRanges: codes.revenueRanges,
        maxDailyUnits: adjudication.maxDailyUnits,
        countsTowardOutOfPocket: adjudication.countsTowardOutOfPocket,
        taxonomyMismatch: adjudication.taxonomyMismatch,
        rules: adjudication.rules,
        humanDescription: adjudication.humanDescription,
    };
};

const sameRules = (left, right) => JSON.stringify(left || []) === JSON.stringify(right || []);

const writePlanService = (service, part, catalog, cost, side) => {
    CODE_FIELDS.forEach((field) => {
        if (part[field]?.length) service[field] = structuredClone(part[field]);
        else delete service[field];
    });
    cost.forEach((raw, slot) => {
        const column = (side === "oon" ? 5 : 1) + slot;
        if (slot === 1 || slot === 2) writeCostCell(service, column, raw);
        else if (raw) writeCostCell(service, column, raw);
    });
    PLAN_ADJUDICATION_FIELDS.forEach((field) => {
        if (part[field] != null && part[field] !== catalog?.[field]) service[field] = part[field];
        else delete service[field];
    });
    const description = part.humanDescription || "";
    const inherits = catalog && sameRules(part.rules, catalog.rules) && description === (catalog.humanDescription || "");
    if (!inherits && part.rules?.length) service.rules = structuredClone(part.rules);
    else delete service.rules;
    if (!inherits && description) service.humanDescription = description;
    else delete service.humanDescription;
};

const PlanServiceDialog = ({ plan, serviceKey, editPlan, onClose }) => {
    const catalog = loadServices();
    const used = new Set(serviceKeys(plan));
    const [source, setSource] = useState(serviceKey || "");
    const sourceEntry = catalog.find((item) => item.key === source) || null;
    const [form, setForm] = useState(() => (serviceKey ? planServiceForm(plan, serviceKey, sourceEntry) : null));
    const [cost, setCost] = useState(() => ({
        inn: serviceKey ? sideCells(plan.inNetwork?.[serviceKey]) : ["Yes", "", "", "Yes"],
        oon: serviceKey ? sideCells(plan.outOfNetwork?.[serviceKey]) : ["", "", "", ""],
    }));
    const [error, setError] = useState("");
    const addingNew = source === NEW_SERVICE;

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const choose = (next) => {
        setSource(next);
        setError("");
        if (!next) {
            setForm(null);
            return;
        }
        const entry = catalog.find((item) => item.key === next);
        setForm(entry ? formFromService(entry) : emptyForm());
    };

    const setField = (field) => (value) => setForm((current) => (current ? { ...current, [field]: value } : current));
    const setSlot = (side, slot) => (value) => setCost((current) => ({
        ...current,
        [side]: current[side].map((item, index) => (index === slot ? value : item)),
    }));

    const save = () => {
        if (!form) return;
        const name = form.name.trim();
        if (!name) {
            setError("Give the service a name.");
            return;
        }
        const startKey = serviceKey || (addingNew ? "" : source);
        const key = startKey && name === words(startKey) ? startKey : camelKey(name);
        if (!key || SKIP_SERVICE.has(key)) {
            setError("Give the service a different name.");
            return;
        }
        if (key !== serviceKey && used.has(key)) {
            setError("This plan already has that service.");
            return;
        }
        const part = serviceFromForm(form, key);
        let entry = catalog.find((item) => item.key === key) || null;
        if (!entry) {
            entry = part;
            saveServices([...catalog, entry]);
        }
        editPlan((target) => {
            if (serviceKey && key !== serviceKey) {
                ["inNetwork", "outOfNetwork"].forEach((sideName) => {
                    const bag = target[sideName];
                    if (!bag || bag[serviceKey] == null) return;
                    bag[key] = bag[serviceKey];
                    delete bag[serviceKey];
                });
            }
            [["inn", "inNetwork"], ["oon", "outOfNetwork"]].forEach(([side, sideName]) => {
                const existing = target[sideName]?.[key];
                if (!existing && side === "oon" && cost.oon.every((item) => !item)) return;
                target[sideName] = target[sideName] || {};
                const service = existing || { isCovered: true };
                target[sideName][key] = service;
                writePlanService(service, part, entry, cost[side], side);
            });
        });
        onClose();
    };

    const options = [
        ...catalog
            .filter((item) => item.key === serviceKey || !used.has(item.key))
            .map((item) => ({ value: item.key, label: words(item.key), description: codeText(item) })),
        { value: NEW_SERVICE, label: "New service", description: "Not in the catalog yet. Saving adds it to the catalog too." },
    ];

    return createPortal(
        <div className="internal-work__dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="internal-work internal-work__dialog is-drawer" role="dialog" aria-modal="true" aria-label={serviceKey ? `Edit ${words(serviceKey)}` : "Add a service to this plan"}>
                <h2>{serviceKey ? words(serviceKey) : "Add a service to this plan"}</h2>
                {serviceKey ? null : (
                    <div className="internal-work__fields">
                        <PickField
                            label="Start from"
                            value={source}
                            options={options}
                            onChange={choose}
                            placeholder="Search the service catalog"
                            isClearable={false}
                            isSearchable
                        />
                    </div>
                )}
                {form ? (
                    <>
                        <h3 className="internal-work__subhead">Cost share on this plan</h3>
                        {[["inn", "In network"], ["oon", "Out of network"]].map(([side, label]) => (
                            <div className="internal-work__dialog-cost" key={side}>
                                <b>{label}</b>
                                <div className="internal-work__fields is-quad">
                                    {COST_SLOTS.map((slotLabel, slot) => (
                                        slot === 0 || slot === 3 ? (
                                            <PickField
                                                key={slotLabel}
                                                label={slotLabel}
                                                value={cost[side][slot]}
                                                options={YES_NO_OPTIONS}
                                                onChange={setSlot(side, slot)}
                                            />
                                        ) : (
                                            <div className="internal-work__field" key={slotLabel}>
                                                <span>{slotLabel}</span>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    aria-label={`${label} ${slotLabel}`}
                                                    placeholder={slot === 1 ? "$25" : "20%"}
                                                    value={cost[side][slot]}
                                                    onChange={(event) => setSlot(side, slot)(event.target.value)}
                                                />
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                        ))}
                        <ServiceFields
                            form={form}
                            setField={setField}
                            rulesKey={`${source}-rules`}
                            billingLocked={!addingNew && Boolean(catalog.find((item) => item.key === (serviceKey || source)))}
                        />
                    </>
                ) : (
                    <p className="internal-work__adj-codes">Pick a catalog service to bring in its codes, where it is billed, and its adjudication. Then set what the member pays on this plan.</p>
                )}
                {error ? <p className="internal-work__service-error">{error}</p> : null}
                <div className="internal-work__form-actions">
                    <button type="button" className="internal-work__reset" onClick={onClose}>Cancel</button>
                    <button type="button" className="internal-work__submit" disabled={!form} onClick={save}>
                        {serviceKey ? "Save service" : "Add to plan"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

const serviceRows = (plan, query = "") => {
    const needle = query.trim().toLowerCase();
    const matches = (row) => !needle || row.label.toLowerCase().includes(needle);
    const named = serviceKeys(plan).map((key) => ({
        kind: "service",
        key,
        label: words(key),
        inn: plan.inNetwork?.[key],
        oon: plan.outOfNetwork?.[key],
    }));
    const excludedNames = [...new Set([
        ...Object.keys(plan.inNetwork?.excludedServices || {}),
        ...Object.keys(plan.outOfNetwork?.excludedServices || {}),
    ])];
    const excluded = excludedNames.map((key) => ({
        kind: "excluded",
        key,
        label: words(key),
        inn: plan.inNetwork?.excludedServices?.[key],
        oon: plan.outOfNetwork?.excludedServices?.[key],
    }));
    const coverage = (plan.coverage || []).map((line, index) => ({
        kind: "coverage",
        key: `${line.name}-${index}`,
        index,
        label: words(line.name) || "New service",
        inn: line.network === "outOfNetwork" ? null : line,
        oon: line.network === "inNetwork" ? null : line,
    }));
    return [...named, ...excluded, ...coverage].filter(matches).flatMap((row) => [
        ...withVariants(row),
        ...visitRows(row),
        ...erShareRows(row),
        ...maxDaysRow(row),
        ...(row.key === "emergencyRoom" ? penaltyRow(row, "nonEmergentPenalty", "Non-emergent penalty") : []),
        ...(row.key === "specialistVisit" ? penaltyRow(row, "noReferralPenalty", "No referral penalty") : []),
        ...conditionPenaltyRow(row),
        ...precertRows(row),
        ...(["service", "excluded", "coverage"].includes(row.kind) ? [{
            kind: "adjudication",
            parentKind: row.kind,
            parentKey: row.key,
            coverageIndex: row.index,
            inn: row.inn,
            oon: row.oon,
        }] : []),
    ]);
};

const formSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 40 }),
    menu: (base) => ({
        ...base,
        marginTop: 4,
        overflow: "hidden",
        border: "1px solid var(--lighterGrey, #e0e0e0)",
        borderRadius: "0.4rem",
        backgroundColor: "#fff",
        boxShadow: "none",
        font: "inherit",
    }),
    menuList: (base) => ({ ...base, padding: 4 }),
    control: (base, state) => ({
        ...base,
        minHeight: "2.5rem",
        height: "2.5rem",
        borderRadius: "0.4rem",
        borderColor: "var(--lighterGrey, #e0e0e0)",
        backgroundColor: state.isDisabled ? "#f2f2f2" : state.isFocused ? "#f7f7f7" : "#fff",
        boxShadow: "none",
        font: "inherit",
        cursor: state.isDisabled ? "default" : "text",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 0.7rem" }),
    placeholder: (base) => ({ ...base, color: "var(--darkGrey, #666)" }),
    singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled ? "var(--darkGrey, #666)" : "var(--darkerGrey, #333)",
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "var(--darkerGrey, #333)" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: "0 0.55rem",
        color: "var(--darkGrey, #666)",
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: "var(--lighterGrey, #e0e0e0)",
        borderRadius: "0.3rem",
    }),
    multiValueLabel: (base) => ({ ...base, color: "var(--darkerGrey, #333)" }),
    multiValueRemove: (base) => ({
        ...base,
        color: "var(--darkerGrey, #333)",
        ":hover": { backgroundColor: "transparent", color: "var(--darkerGrey, #333)" },
    }),
    option: (base, state) => ({
        ...base,
        borderRadius: "0.3rem",
        color: "var(--darkerGrey, #333)",
        backgroundColor: state.isFocused || state.isSelected ? "#f7f7f7" : "#fff",
        cursor: "pointer",
    }),
};

const variantSelectStyles = {
    ...formSelectStyles,
    control: (base, state) => ({
        ...formSelectStyles.control(base, state),
        height: "auto",
    }),
    menu: (base) => ({
        ...formSelectStyles.menu(base),
        minWidth: "20rem",
    }),
    groupHeading: (base) => ({
        ...base,
        padding: "0.45rem 0.75rem 0.2rem",
        fontSize: "0.75rem",
        fontWeight: 650,
        color: "var(--darkGrey, #666)",
        textTransform: "none",
    }),
};

const serviceSelectStyles = {
    ...formSelectStyles,
    menu: (base) => ({
        ...formSelectStyles.menu(base),
        minWidth: "26rem",
    }),
    option: (base, state) => ({
        ...formSelectStyles.option(base, state),
        padding: "0.55rem 0.75rem",
    }),
};

const EditableCell = ({ value, kind, onCommit, serviceKey, usedKeys, placesOfService, modifiers, placeholder, choices }) => {
    const canEdit = useCanEdit();
    const anchorRef = useRef(null);
    const [menuHeight, setMenuHeight] = useState(320);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    useLayoutEffect(() => {
        if (!editing || kind !== "service" || !anchorRef.current) return;
        anchorRef.current.scrollIntoView({ block: "center", inline: "nearest" });
        const rect = anchorRef.current.getBoundingClientRect();
        const room = Math.max(rect.top, window.innerHeight - rect.bottom) - 16;
        setMenuHeight(Math.min(420, Math.max(320, room)));
    }, [editing, kind]);
    useEffect(() => {
        if (!canEdit) setEditing(false);
    }, [canEdit]);
    useEffect(() => {
        if (!editing) setDraft(value || "");
    }, [value, editing]);
    const emptyVariant = kind === "variant" && !value;
    const blank = !value ? (emptyVariant ? "Place of service" : (placeholder || "")) : "";
    const shown = value || blank;
    if (!canEdit) {
        return <span className={`internal-work__cell is-locked${blank ? " is-empty" : ""}`}>{shown}</span>;
    }
    if (!editing) {
        return (
            <button type="button" className={`internal-work__cell${blank ? " is-empty" : ""}`} onClick={() => setEditing(true)}>
                {shown}
            </button>
        );
    }
    const finish = (next) => {
        onCommit(next);
        setEditing(false);
    };
    if (kind === "bool") {
        const options = [
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
        ];
        return (
            <Select
                autoFocus
                openMenuOnFocus
                className="react-select-container"
                classNamePrefix="react-select"
                options={options}
                value={options.find((option) => option.value === draft) || null}
                onChange={(option) => finish(option ? option.value : "")}
                onBlur={() => setEditing(false)}
                isSearchable={false}
                placeholder=""
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                styles={formSelectStyles}
            />
        );
    }
    if (kind === "choice") {
        const options = choices || [];
        return (
            <Select
                autoFocus
                openMenuOnFocus
                className="react-select-container"
                classNamePrefix="react-select"
                options={options}
                value={options.find((option) => option.value === draft) || null}
                onChange={(option) => finish(option ? option.value : "")}
                onBlur={() => setEditing(false)}
                isSearchable={false}
                placeholder={placeholder || ""}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                styles={formSelectStyles}
            />
        );
    }
    if (kind === "variant") {
        const selected = [
            ...(placesOfService || []).map((code) => (
                VARIANT_OPTION_LIST.find((option) => option.kind === "place" && option.value === code) || { value: code, label: code, kind: "place" }
            )),
            ...(modifiers || []).map((code) => (
                VARIANT_OPTION_LIST.find((option) => option.kind === "modifier" && option.value === code) || { value: code, label: code, kind: "modifier" }
            )),
        ];
        return (
            <Select
                autoFocus
                isMulti
                openMenuOnFocus
                className="react-select-container"
                classNamePrefix="react-select"
                options={VARIANT_OPTIONS}
                value={selected}
                onChange={(options) => {
                    const next = options || [];
                    const modifierCodes = new Set(COVERAGE_MODIFIER.map(([code]) => code));
                    onCommit({
                        placesOfService: next.filter((option) => !modifierCodes.has(option.value)).map((option) => option.value),
                        modifiers: next.filter((option) => modifierCodes.has(option.value)).map((option) => option.value),
                    });
                }}
                onBlur={() => {
                    window.setTimeout(() => setEditing(false), 0);
                }}
                onKeyDown={(event) => {
                    if (event.key === "Escape") setEditing(false);
                }}
                placeholder="Place of service"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuPlacement="auto"
                styles={variantSelectStyles}
            />
        );
    }
    if (kind === "service") {
        const used = new Set(usedKeys || []);
        const catalog = loadServices();
        const options = catalog
            .filter((item) => item.key === serviceKey || !used.has(item.key))
            .map((item) => ({ value: item.key, label: words(item.key), catalog: item }));
        const selected = options.find((option) => option.label === value) || (value ? { value, label: value } : null);
        return (
            <div className="internal-work__service-anchor" ref={anchorRef}>
            <CreatableSelect
                autoFocus
                openMenuOnFocus
                minMenuHeight={320}
                maxMenuHeight={menuHeight}
                className="react-select-container"
                classNamePrefix="react-select"
                options={options}
                value={selected}
                onChange={(option) => {
                    if (!option) return;
                    finish(option.__isNew__ ? option.value.trim() : option.catalog);
                }}
                onBlur={() => setEditing(false)}
                onKeyDown={(event) => {
                    if (event.key === "Escape") setEditing(false);
                }}
                placeholder="Search services"
                formatCreateLabel={(input) => `Add "${input}"`}
                isValidNewOption={(input) => {
                    const typed = input.trim().toLowerCase();
                    if (!typed) return false;
                    return !catalog.some((item) => words(item.key).toLowerCase() === typed);
                }}
                filterOption={(option, input) => {
                    const query = String(input || "").trim().toLowerCase();
                    if (!query) return true;
                    const data = option?.data || option;
                    const label = String(option?.label ?? data?.label ?? "");
                    const codes = data?.catalog ? codeText(data.catalog) : "";
                    return `${label} ${codes}`.toLowerCase().includes(query);
                }}
                formatOptionLabel={(option, meta) => (
                    meta.context === "menu" && option.catalog
                        ? (
                            <span>
                                {option.label}
                                <span className="internal-work__service-codes">{codeText(option.catalog)}</span>
                            </span>
                        )
                        : option.label
                )}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuPlacement="auto"
                styles={serviceSelectStyles}
            />
            </div>
        );
    }
    return (
        <input
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => finish(draft)}
            onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") setEditing(false);
            }}
        />
    );
};

const camelKey = (label) => {
    const parts = String(label || "").trim().split(/[^A-Za-z0-9]+/).filter(Boolean);
    return parts.map((part, index) => (
        index === 0 ? part.charAt(0).toLowerCase() + part.slice(1) : part.charAt(0).toUpperCase() + part.slice(1)
    )).join("");
};

const readSide = (target, row, side) => {
    if (row.kind === "coverage") {
        const line = target.coverage?.[row.index];
        if (!line) return null;
        if (side === "inn" && line.network === "outOfNetwork") return null;
        if (side === "oon" && line.network === "inNetwork") return null;
        return line;
    }
    const root = side === "inn" ? target.inNetwork : target.outOfNetwork;
    if (!root) return null;
    if (row.kind === "excluded") return root.excludedServices?.[row.key] || null;
    return root[row.key] || null;
};

const ensureSide = (target, row, side) => {
    const found = readSide(target, row, side);
    if (found) return found;
    if (row.kind === "coverage") {
        const line = target.coverage[row.index];
        const created = { name: line?.name || "New service", network: side === "oon" ? "outOfNetwork" : "inNetwork", isCovered: true };
        target.coverage.push(created);
        return created;
    }
    const root = side === "inn" ? target.inNetwork : target.outOfNetwork;
    if (!root) return null;
    if (row.kind === "excluded") {
        root.excludedServices = root.excludedServices || {};
        root.excludedServices[row.key] = { isCovered: false };
        return root.excludedServices[row.key];
    }
    root[row.key] = { isCovered: true };
    return root[row.key];
};

const parentRow = (row) => ({
    kind: row.parentKind,
    key: row.parentKey,
    index: row.coverageIndex,
});

const setMemberCost = (service, type, raw) => {
    const cleaned = String(raw || "").replace(/[$,%\s]/g, "");
    const costs = (service.costs || []).filter((cost) => cost.type !== type);
    if (cleaned !== "" && !Number.isNaN(Number(cleaned))) costs.push({ type, value: Number(cleaned) });
    if (costs.length) service.costs = costs;
    else delete service.costs;
};

const writeCostCell = (service, column, raw) => {
    const field = column % 4;
    if (field === 1) service.isCovered = raw !== "No";
    else if (field === 2) setMemberCost(service, "copayAmount", raw);
    else if (field === 3) setMemberCost(service, "copayPercent", raw);
    else service.deductibleApplies = raw !== "No";
};

const ensureVariant = (target, row, side) => {
    const parent = ensureSide(target, parentRow(row), side);
    if (!parent) return null;
    parent.variants = parent.variants || [];
    while (parent.variants.length <= row.variantIndex) {
        const other = side === "inn" ? "oon" : "inn";
        const sibling = readSide(target, parentRow(row), other)?.variants?.[parent.variants.length];
        const created = { isCovered: true };
        if (sibling?.placesOfService?.length) created.placesOfService = [...sibling.placesOfService];
        if (sibling?.modifiers?.length) created.modifiers = [...sibling.modifiers];
        parent.variants.push(created);
    }
    return parent.variants[row.variantIndex];
};

const addServiceVariant = (target, row) => {
    const service = ensureSide(target, row, "inn");
    if (!service) return;
    const variant = { isCovered: service.isCovered !== false };
    if (service.deductibleApplies != null) variant.deductibleApplies = service.deductibleApplies;
    if (service.costs?.length) variant.costs = structuredClone(service.costs);
    service.variants = [...(service.variants || []), variant];
};

const removeServiceVariant = (target, row) => {
    ["inn", "oon"].forEach((side) => {
        const service = readSide(target, parentRow(row), side);
        if (!service?.variants || service.variants.length <= row.variantIndex) return;
        service.variants.splice(row.variantIndex, 1);
        if (!service.variants.length) delete service.variants;
    });
};

const CODE_FIELDS = ["cptCodes", "cptCodeGroups", "hcpcsCodes", "hcpcsCodeGroups", "revenueCode", "revenueCodeGroups"];

const applyCatalogCodes = (service, catalog) => {
    CODE_FIELDS.forEach((field) => {
        if (catalog[field]?.length) service[field] = structuredClone(catalog[field]);
        else delete service[field];
    });
};

const keyInUse = (target, key, row) => {
    if (!key || SKIP_SERVICE.has(key)) return true;
    if (row.kind === "service" && row.key === key) return false;
    return Boolean(target.inNetwork?.[key] || target.outOfNetwork?.[key]);
};

const detachService = (target, row) => {
    if (row.kind === "coverage") {
        const line = target.coverage?.[row.index];
        if (!line) return [];
        target.coverage.splice(row.index, 1);
        const service = { ...line };
        const side = service.network === "outOfNetwork" ? "outOfNetwork" : "inNetwork";
        delete service.name;
        delete service.network;
        return [{ side, service }];
    }
    const sides = ["inNetwork", "outOfNetwork"];
    if (row.kind === "excluded") {
        return sides.flatMap((side) => {
            const bag = target[side]?.excludedServices;
            if (!bag || bag[row.key] == null) return [];
            const service = bag[row.key];
            delete bag[row.key];
            return [{ side, service }];
        });
    }
    return sides.flatMap((side) => {
        const bag = target[side];
        if (!bag || bag[row.key] == null) return [];
        const service = bag[row.key];
        delete bag[row.key];
        return [{ side, service }];
    });
};

const attachNamedService = (target, key, pieces, catalog) => {
    const placed = pieces.length ? pieces : [{ side: "inNetwork", service: { isCovered: true } }];
    placed.forEach(({ side, service }) => {
        target[side] = target[side] || {};
        applyCatalogCodes(service, catalog);
        target[side][key] = service;
    });
};

const writePenalty = (service, key, valueType, raw) => {
    const cleaned = String(raw || "").replace(/[$,%\s]/g, "");
    const condition = service[key]?.condition;
    if (cleaned === "" || Number.isNaN(Number(cleaned))) {
        if (condition) service[key] = { condition };
        else delete service[key];
        return;
    }
    const penalty = { valueType, value: Number(cleaned) };
    if (key === "noReferralPenalty") penalty.type = "penalty";
    if (condition) penalty.condition = condition;
    service[key] = penalty;
};

const commitServiceCell = (target, row, column, raw) => {
    if (row.kind === "penalty") {
        const field = column % 4;
        if (column === 11 && row.condition) {
            const condition = String(raw || "").trim();
            ["inn", "oon"].forEach((side) => {
                const service = side === "inn" ? ensureSide(target, parentRow(row), side) : readSide(target, parentRow(row), side);
                if (!service) return;
                const current = service[row.penaltyKey] || {};
                if (condition) service[row.penaltyKey] = { ...current, condition };
                else if (current.value == null) delete service[row.penaltyKey];
                else {
                    delete current.condition;
                    service[row.penaltyKey] = current;
                }
            });
            return;
        }
        if (column === 0 || column >= 9 || field === 0 || field === 1) return;
        const service = ensureSide(target, parentRow(row), column < 5 ? "inn" : "oon");
        if (service) writePenalty(service, row.penaltyKey, field === 3 ? "percent" : "amount", raw);
        return;
    }
    if (row.kind === "maxDays") {
        if (column !== 2) return;
        const cleaned = String(raw || "").replace(/[^\d.]/g, "");
        const days = cleaned === "" || Number.isNaN(Number(cleaned)) ? null : Number(cleaned);
        ["inn", "oon"].forEach((side) => {
            const service = side === "inn" ? ensureSide(target, parentRow(row), side) : readSide(target, parentRow(row), side);
            if (!service) return;
            if (days == null) delete service.maxNumberOfDaysCovered;
            else service.maxNumberOfDaysCovered = days;
        });
        return;
    }
    if (row.kind === "visit") {
        if (column === 0 || column === 9 || column === 10 || (column === 11 && !row.memberPcp)) return;
        const parent = parentRow(row);
        const field = row.field;
        if (column === 11) {
            const rendered = raw === "Yes";
            ["inn", "oon"].forEach((side) => {
                const service = side === "inn" ? ensureSide(target, parent, side) : readSide(target, parent, side);
                if (!service) return;
                service[field] = { ...(service[field] || {}), renderedByMemberPcp: rendered };
            });
            return;
        }
        const service = ensureSide(target, parent, column < 5 ? "inn" : "oon");
        if (!service) return;
        const otherSide = column < 5 ? "oon" : "inn";
        const other = readSide(target, parent, otherSide)?.[field]?.renderedByMemberPcp;
        service[field] = service[field] || {};
        if (row.memberPcp && service[field].renderedByMemberPcp == null && other != null) {
            service[field].renderedByMemberPcp = other;
        }
        writeCostCell(service[field], column, raw);
        return;
    }
    if (row.kind === "precert") {
        if (column === 0 || column === 9 || column === 10 || column % 4 === 0) return;
        const parent = parentRow(row);
        if (column === 11) {
            const responsibility = raw === "Member" ? "member" : raw === "Provider" ? "provider" : "";
            ["inn", "oon"].forEach((side) => {
                const service = side === "inn" ? ensureSide(target, parent, side) : readSide(target, parent, side);
                if (!service) return;
                service.precertification = { ...(service.precertification || {}) };
                if (responsibility) service.precertification.responsibility = responsibility;
                else delete service.precertification.responsibility;
            });
            return;
        }
        const service = ensureSide(target, parent, column < 5 ? "inn" : "oon");
        if (!service) return;
        service.precertification = service.precertification || {};
        const slot = column % 4;
        if (slot === 1) service.precertification.required = raw !== "No";
        else {
            const holder = {};
            writePenalty(holder, "penalty", slot === 3 ? "percent" : "amount", raw);
            if (holder.penalty) service.precertification.penalty = holder.penalty;
            else delete service.precertification.penalty;
        }
        return;
    }
    if (row.kind === "variant") {
        if (column === 0) {
            const places = Array.isArray(raw?.placesOfService) ? raw.placesOfService : [];
            const modifiers = Array.isArray(raw?.modifiers) ? raw.modifiers : [];
            ["inn", "oon"].forEach((side) => {
                const variant = readSide(target, parentRow(row), side)?.variants?.[row.variantIndex];
                if (!variant) return;
                if (places.length) variant.placesOfService = places;
                else delete variant.placesOfService;
                if (modifiers.length) variant.modifiers = modifiers;
                else delete variant.modifiers;
            });
            return;
        }
        if (column >= 9) return;
        const variant = ensureVariant(target, row, column < 5 ? "inn" : "oon");
        if (variant) writeCostCell(variant, column, raw);
        return;
    }
    if (column === 0) {
        const catalog = raw && typeof raw === "object" ? raw : null;
        const next = catalog ? words(catalog.key) : String(raw || "").trim();
        if (!next) return;
        if (catalog) {
            if (keyInUse(target, catalog.key, row)) return;
            if (row.kind === "service" && row.key === catalog.key) {
                ["inNetwork", "outOfNetwork"].forEach((side) => {
                    const service = target[side]?.[catalog.key];
                    if (service) applyCatalogCodes(service, catalog);
                });
                return;
            }
            attachNamedService(target, catalog.key, detachService(target, row), catalog);
            return;
        }
        if (next === row.label) return;
        if (row.kind === "coverage") {
            target.coverage[row.index].name = next;
            return;
        }
        const key = camelKey(next);
        if (keyInUse(target, key, row)) return;
        const move = (bag) => {
            if (!bag || bag[row.key] == null || !key || key === row.key) return;
            bag[key] = bag[row.key];
            delete bag[row.key];
        };
        if (row.kind === "excluded") {
            move(target.inNetwork?.excludedServices);
            move(target.outOfNetwork?.excludedServices);
        } else {
            move(target.inNetwork);
            move(target.outOfNetwork);
        }
        return;
    }
    const textColumns = { 9: "limits", 10: "codes", 11: "notes" };
    if (textColumns[column]) {
        const inn = readSide(target, row, "inn");
        const oon = readSide(target, row, "oon");
        const shown = (service) => {
            if (column === 9) return (service?.limits || []).map(limitText).filter(Boolean).join(" · ");
            if (column === 10) return codeText(service);
            return noteText(service);
        };
        if (String(raw ?? "") === bothSides(shown(inn), shown(oon))) return;
        const services = [];
        [inn, oon].forEach((service) => {
            if (service && !services.includes(service)) services.push(service);
        });
        if (!services.length) {
            const created = ensureSide(target, row, "inn");
            if (created) services.push(created);
        }
        services.forEach((service) => {
            if (column === 9) {
                const text = String(raw || "").trim();
                const sharedMatch = text.match(/\s+shared with\s+(.+)$/i);
                const head = sharedMatch ? text.slice(0, sharedMatch.index).trim() : text;
                const match = head.match(/^(\$?[\d,]+)\s*(days \/ year|\/ year|lifetime)?/i);
                if (!match) {
                    service.limits = [];
                    return;
                }
                const value = Number(match[1].replace(/[$,]/g, ""));
                const kind = match[2] || "";
                const type = /day/i.test(kind) ? "daysPerPlanYear" : /lifetime/i.test(kind) ? "dollarsPerLifetime" : "dollarsPerPlanYear";
                const limit = { type, value };
                if (sharedMatch) {
                    const catalog = loadServices();
                    const sharedWith = sharedMatch[1].split(",").map((part) => part.trim()).filter(Boolean).map((part) => {
                        const found = catalog.find((item) => (
                            words(item.key).toLowerCase() === part.toLowerCase() || item.key.toLowerCase() === part.toLowerCase()
                        ));
                        return found?.key || camelKey(part);
                    }).filter(Boolean);
                    if (sharedWith.length) limit.sharedWith = sharedWith;
                }
                service.limits = [limit];
            } else if (column === 10) {
                service.cptCodes = String(raw || "").split(",").map((code) => code.trim()).filter(Boolean);
                delete service.hcpcsCodes;
                delete service.revenueCode;
                delete service.cptCodeGroups;
                delete service.hcpcsCodeGroups;
                delete service.revenueCodeGroups;
            } else {
                const note = String(raw || "").trim();
                service.notes = note ? [note] : [];
                service.conditions = [];
            }
        });
        return;
    }
    const side = column < 5 ? "inn" : "oon";
    const service = ensureSide(target, row, side);
    if (!service) return;
    writeCostCell(service, column, raw);
};

const RX_TIERS = ["generic", "preferred", "nonPrefered", "speciality"];
const BRAND_PENALTY = [
    { value: "none", label: "No penalty", description: "The member pays the brand copay." },
    { value: "memberPaysDifference", label: "Member pays the difference", description: "When a generic is available, the member pays the brand copay plus the price difference." },
];
const RX_DEFAULT_RULES = [
    { join: "if", when: "The drug is on the free preventive drug list", then: "The member pays $0, even before the deductible." },
    { join: "elseIf", when: "The drug is insulin", then: "The member pays no more than the insulin cap for a 30-day supply." },
    { join: "elseIf", when: "The drug is a specialty drug and the plan uses one specialty pharmacy", then: "Fill it at that pharmacy. Deny it anywhere else." },
    { join: "else", then: "Charge the member the copay for the drug's tier and supply." },
];

const moneyText = (value) => `$${Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const rxCostLabel = (cost) => {
    if (!cost || cost.value == null) return "";
    if (cost.type !== "copayPercent") return moneyText(cost.value);
    const bounds = cost.min != null && cost.max != null
        ? ` (${moneyText(cost.min)}–${moneyText(cost.max)})`
        : cost.min != null ? ` (min ${moneyText(cost.min)})` : cost.max != null ? ` (max ${moneyText(cost.max)})` : "";
    return `${cost.value}%${bounds}`;
};

const parseRxCost = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return null;
    const percent = text.match(/(\d+(?:\.\d+)?)\s*%/);
    if (!percent) {
        const amount = Number(text.replace(/[$,\s]/g, ""));
        return Number.isNaN(amount) ? undefined : { type: "copayAmount", value: amount };
    }
    const cost = { type: "copayPercent", value: Number(percent[1]) };
    const rest = text.slice(percent.index + percent[0].length);
    const range = rest.match(/\$?\s*(\d+(?:\.\d+)?)\s*[–-]\s*\$?\s*(\d+(?:\.\d+)?)/);
    const min = rest.match(/min\w*\s*\$?\s*(\d+(?:\.\d+)?)/i);
    const max = rest.match(/max\w*\s*\$?\s*(\d+(?:\.\d+)?)/i);
    if (range) {
        cost.min = Number(range[1]);
        cost.max = Number(range[2]);
    } else {
        if (min) cost.min = Number(min[1]);
        if (max) cost.max = Number(max[1]);
    }
    return cost;
};

const RxForm = ({ plan, name, editPlan }) => {
    const canEdit = useCanEdit();
    const rx = plan.inNetwork?.rx || {};
    const programs = rx.programs || {};
    const separateDeductible = plan.accumulators?.rxDeductibleCountsTowardMedical === false;
    const separateOop = plan.accumulators?.rxOutOfPocketCountsTowardMedical === false;
    const custom = Boolean(rx.rules?.length || rx.humanDescription);
    const editRx = (recipe) => editPlan((target) => {
        target.inNetwork = target.inNetwork || {};
        target.inNetwork.rx = target.inNetwork.rx || {};
        recipe(target.inNetwork.rx);
    });
    const writeSupply = (tier, supply, raw) => editRx((next) => {
        const cost = parseRxCost(raw);
        if (cost === undefined) return;
        next.tier = next.tier || {};
        next.tier[tier] = next.tier[tier] || {};
        if (cost) next.tier[tier][supply] = cost;
        else delete next.tier[tier][supply];
        if (!Object.keys(next.tier[tier]).length) delete next.tier[tier];
    });
    const writeDeductible = (tier, raw) => editRx((next) => {
        next.tier = next.tier || {};
        next.tier[tier] = next.tier[tier] || {};
        if (raw === "") delete next.tier[tier].deductibleApplies;
        else next.tier[tier].deductibleApplies = raw === "Yes";
    });
    const setProgram = (field, value) => editRx((next) => {
        next.programs = { ...(next.programs || {}) };
        if (value == null || value === "") delete next.programs[field];
        else next.programs[field] = value;
        if (!Object.keys(next.programs).length) delete next.programs;
    });
    return (
        <>
            <div className="internal-work__sheet">
                <InternalTable
                    columns={["Tier", ...SUPPLIES.map(([, label]) => label), "Ded"]}
                    rows={RX_TIERS.map((tier) => {
                        const supplies = rx.tier?.[tier] || {};
                        return {
                            key: tier,
                            cells: [
                                words(tier),
                                ...SUPPLIES.map(([supply]) => (
                                    <EditableCell
                                        key={`${tier}-${supply}`}
                                        value={rxCostLabel(supplies[supply])}
                                        onCommit={(next) => writeSupply(tier, supply, next)}
                                    />
                                )),
                                <EditableCell
                                    key={`${tier}-ded`}
                                    kind="bool"
                                    value={supplies.deductibleApplies == null ? "" : supplies.deductibleApplies ? "Yes" : "No"}
                                    onCommit={(next) => writeDeductible(tier, next)}
                                />,
                            ],
                        };
                    })}
                />
            </div>
            <p className="internal-work__adj-codes">A percent tier can set a floor and a cap, like 20% ($10–$100).</p>
            {(separateDeductible || separateOop) && (
                <div className="internal-work__matrix">
                    <span />
                    <span>Individual</span>
                    <span>Family</span>
                    {separateDeductible && (
                        <>
                            <span>Rx deductible</span>
                            <QuietInput label="Rx deductible, individual" name={`${name}.inNetwork.rx.deductible.individual`} numeric />
                            <QuietInput label="Rx deductible, family" name={`${name}.inNetwork.rx.deductible.family`} numeric />
                        </>
                    )}
                    {separateOop && (
                        <>
                            <span>Rx out-of-pocket</span>
                            <QuietInput label="Rx out-of-pocket, individual" name={`${name}.inNetwork.rx.outOfPocketMaximum.individual`} numeric />
                            <QuietInput label="Rx out-of-pocket, family" name={`${name}.inNetwork.rx.outOfPocketMaximum.family`} numeric />
                        </>
                    )}
                </div>
            )}
            <div className="internal-work__fields">
                <div className="internal-work__field">
                    <span>Programs</span>
                    <Checks>
                        <Check label="ACA preventive drugs at $0" name={`${name}.inNetwork.rx.programs.acaPreventiveAtZero`} />
                        <Check label="Specialty pharmacy required" name={`${name}.inNetwork.rx.programs.specialtyPharmacyRequired`} />
                    </Checks>
                </div>
                <Field
                    label="Insulin cap, 30 days"
                    name={`${name}.inNetwork.rx.programs.insulinCap`}
                    numeric
                    hint="The most a member pays for a 30-day supply of insulin. Many states require a cap, and some set it at $35."
                />
                <div className="internal-work__field">
                    <span>
                        Brand when a generic exists
                        <FieldHint text="What the member pays when they pick a brand drug and a generic is available." />
                    </span>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!canEdit}
                        isClearable
                        isSearchable={false}
                        options={BRAND_PENALTY}
                        value={BRAND_PENALTY.find((option) => option.value === programs.brandPenalty) || null}
                        onChange={(option) => setProgram("brandPenalty", option?.value)}
                        formatOptionLabel={(option, { context }) => (
                            context === "menu" ? (
                                <span className="internal-work__menu-option">
                                    <span>{option.label}</span>
                                    <small>{option.description}</small>
                                </span>
                            ) : option.label
                        )}
                        placeholder="Choose"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={{ ...formSelectStyles, menu: (base) => ({ ...formSelectStyles.menu(base), minWidth: "20rem" }) }}
                    />
                </div>
            </div>
            <div className="internal-work__adj-head">
                <span>{custom ? "Rx rules for this plan" : "Standard Rx rules"}</span>
                {canEdit && !custom ? (
                    <button type="button" onClick={() => editRx((next) => { next.rules = structuredClone(RX_DEFAULT_RULES); })}>Customize for this plan</button>
                ) : null}
                {canEdit && custom ? (
                    <button type="button" onClick={() => editRx((next) => { delete next.rules; delete next.humanDescription; })}>Use the standard</button>
                ) : null}
            </div>
            <RuleLines
                key={custom ? "plan" : "standard"}
                rules={custom ? rx.rules || [] : RX_DEFAULT_RULES}
                readOnly={!canEdit || !custom}
                onChange={(next) => editRx((target) => {
                    const lines = next.filter((rule) => rule.when || rule.then);
                    if (lines.length) target.rules = lines;
                    else delete target.rules;
                })}
            />
            <div className="internal-work__field">
                <span>In plain language</span>
                <textarea
                    className="internal-work__notes"
                    readOnly={!canEdit}
                    aria-label="Rx in plain language"
                    defaultValue={rx.humanDescription || ""}
                    key={rx.humanDescription || ""}
                    placeholder="How this plan pays for prescriptions, in plain words."
                    onBlur={(event) => {
                        const text = event.target.value.trim();
                        if (text === (rx.humanDescription || "")) return;
                        editRx((next) => {
                            if (text) next.humanDescription = text;
                            else delete next.humanDescription;
                        });
                    }}
                />
            </div>
        </>
    );
};

const PlanForm = ({ plan, name, editPlan }) => {
    const { setValue } = useFormContext();
    const [serviceDialog, setServiceDialog] = useState(null);
    const [serviceQuery, setServiceQuery] = useState("");
    const [openDetails, setOpenDetails] = useState(() => new Set());
    const shownServiceRows = serviceRows(plan, serviceQuery);
    const detailKeys = shownServiceRows.filter((row) => row.kind === "adjudication").map((row) => `${row.parentKind}-${row.parentKey}`);
    const allDetailsOpen = detailKeys.length > 0 && detailKeys.every((key) => openDetails.has(key));
    const toggleDetails = (key) => setOpenDetails((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
    });
    const setAllDetails = (open) => setOpenDetails(open ? new Set(detailKeys) : new Set());
    const setTier = (index, field, current, next) => {
        if (next === current) return;
        setValue(`${name}.tiers.${index}.${field}`, next, { shouldDirty: true });
    };
    const pickTier = (index, currentName, currentCode, next) => {
        const match = CENSUS_TIERS.find((item) => item.tierName === next);
        setTier(index, "tierName", currentName, next);
        if (match) setTier(index, "code", currentCode, match.code);
    };
    return (
        <article className="internal-work__plan-form">
            <div className="internal-work__fields">
                <Field label="Plan name" name={`${name}.planName`} />
                <Field label="Plan ID" name={`${name}.planId`} />
                <Field label="Administration" name={`${name}.planAdministration`} />
                <div className="internal-work__field">
                    <span>Requirements</span>
                    <Checks>
                        <Check label="PCP required" name={`${name}.pcpRequired`} />
                        <Check label="Referral required" name={`${name}.referralRequired`} />
                    </Checks>
                </div>
                <Field
                    label="PCP specialty not elected"
                    name={`${name}.pcpSpecialtyNotElected.value`}
                    numeric
                    hint="The penalty when a member sees a doctor in a PCP specialty who is not the PCP they elected. Family medicine, internal medicine, and pediatrics count as PCP specialties. A visit with the elected PCP uses the PCP cost share. A visit with a different doctor in one of those specialties takes this penalty."
                />
                <div className="internal-work__dates">
                    <DateField label="Effective date" name={`${name}.planEffectiveDate`} />
                    <DateField label="Termination date" name={`${name}.planTerminationDate`} />
                </div>
                <div className="internal-work__field is-end">
                    <Check label="Virtual primary care" name={`${name}.virtualPrimaryCare`} />
                </div>
            </div>
            <SubForm
                title="Custom fees"
                onAdd={() => editPlan((target) => { target.fees = [...(target.fees || []), emptyAdminFee()]; })}
            >
                {(plan.fees || []).map((fee, index) => (
                    <SubForm
                        title={ADMIN_FEES.find((item) => item.value === fee.party)?.label || "Fee"}
                        key={`fee-${fee.party}-${index}`}
                        onRemove={() => editPlan((target) => { target.fees.splice(index, 1); })}
                    >
                        <FeeSchedule
                            name={`${name}.fees.${index}`}
                            item={fee}
                            plan={plan}
                            editPlan={editPlan}
                            listKey="fees"
                            index={index}
                            who={<ChoiceField label="For" name={`${name}.fees.${index}.party`} options={ADMIN_FEES} />}
                        />
                    </SubForm>
                ))}
                {(plan.fees || []).length === 0 && <p>No custom fees yet.</p>}
            </SubForm>
            <SubForm
                title="Census"
                onAdd={() => editPlan((target) => {
                    target.tiers = [...(target.tiers || []), { tierName: "", code: "" }];
                })}
            >
                <div className="internal-work__sheet">
                <InternalTable
                    columns={["Census tier", "Code", "Dependent age", ""]}
                    rows={(plan.tiers || []).map((tier, index) => ({
                        key: `${tier.code || tier.tierName || "tier"}-${index}`,
                        cells: [
                            <EditableCell
                                key="tier"
                                kind="choice"
                                choices={CENSUS_TIERS.map((item) => ({ value: item.tierName, label: item.tierName }))}
                                value={tier.tierName || ""}
                                placeholder="Census tier"
                                onCommit={(next) => pickTier(index, tier.tierName || "", tier.code || "", next)}
                            />,
                            <EditableCell
                                key="code"
                                value={tier.code || ""}
                                onCommit={(next) => setTier(index, "code", tier.code || "", next)}
                            />,
                            tier.dependentAge ? `${tier.dependentAge.value} ${tier.dependentAge.condition}` : "",
                            <IconButton
                                key={`${tier.code || "tier"}-${index}-remove`}
                                label={`Remove ${tier.tierName || "tier"}`}
                                danger
                                onClick={() => editPlan((target) => {
                                    target.tiers.splice(index, 1);
                                })}
                            >
                                <LuTrash2 />
                            </IconButton>,
                        ],
                    }))}
                />
                </div>
            </SubForm>
            <SubForm title="Accumulator">
                <div className="internal-work__fields">
                    <Field label="Deductible" name={`${name}.accumulators.deductible.type`} />
                    <Field label="Out-of-pocket maximum" name={`${name}.accumulators.outOfPocketMaximum.type`} />
                </div>
                <Checks>
                    <Check label="Rx deductible counts toward medical" name={`${name}.accumulators.rxDeductibleCountsTowardMedical`} />
                    <Check label="Rx out-of-pocket counts toward medical" name={`${name}.accumulators.rxOutOfPocketCountsTowardMedical`} />
                </Checks>
                <SubForm title="In network">
                    <Amounts name={`${name}.inNetwork`} side={plan.inNetwork} />
                </SubForm>
                <SubForm title="Out of network">
                    <Amounts name={`${name}.outOfNetwork`} side={plan.outOfNetwork} />
                </SubForm>
            </SubForm>
            <SubForm
                title="Provider network"
                onAdd={() => editPlan((target) => { target.networks = [...(target.networks || []), emptyNetwork()]; })}
            >
                {(plan.networks || []).map((item, index) => (
                    <SubForm
                        title={item.name || "Provider network"}
                        key={`network-${item.name}-${index}`}
                        onRemove={() => editPlan((target) => { target.networks.splice(index, 1); })}
                    >
                        <FeeSchedule
                            name={`${name}.networks.${index}`}
                            item={item}
                            plan={plan}
                            editPlan={editPlan}
                            listKey="networks"
                            index={index}
                            who={<ChoiceField label="Network" name={`${name}.networks.${index}.name`} options={choiceOptions(PROVIDER_NETWORKS)} placeholder="Select a network" />}
                        />
                    </SubForm>
                ))}
                {(plan.networks || []).length === 0 && <p>No provider network yet.</p>}
            </SubForm>
            <SubForm
                title="Telemedicine"
                onAdd={() => editPlan((target) => { target.telemedicine = [...(target.telemedicine || []), emptyVendor()]; })}
            >
                {(plan.telemedicine || []).map((item, index) => (
                    <SubForm
                        title={item.vendor || "Telemedicine"}
                        key={`tele-${item.vendor}-${index}`}
                        onRemove={() => editPlan((target) => { target.telemedicine.splice(index, 1); })}
                    >
                        <FeeSchedule
                            name={`${name}.telemedicine.${index}`}
                            item={item}
                            plan={plan}
                            editPlan={editPlan}
                            listKey="telemedicine"
                            index={index}
                            who={<ChoiceField label="Vendor" name={`${name}.telemedicine.${index}.vendor`} options={choiceOptions(TELEMEDICINE_VENDORS)} placeholder="Select a vendor" />}
                        />
                    </SubForm>
                ))}
                {(plan.telemedicine || []).length === 0 && <p>No telemedicine vendor yet.</p>}
            </SubForm>
            <SubForm
                title="PBM"
                onAdd={() => editPlan((target) => { target.pbm = [...(target.pbm || []), emptyVendor()]; })}
            >
                {(plan.pbm || []).map((item, index) => (
                    <SubForm
                        title={item.vendor || "PBM"}
                        key={`pbm-${item.vendor}-${index}`}
                        onRemove={() => editPlan((target) => { target.pbm.splice(index, 1); })}
                    >
                        <FeeSchedule
                            name={`${name}.pbm.${index}`}
                            item={item}
                            plan={plan}
                            editPlan={editPlan}
                            listKey="pbm"
                            index={index}
                            who={<ChoiceField label="PBM" name={`${name}.pbm.${index}.vendor`} options={choiceOptions(PBM_VENDORS)} placeholder="Select a PBM" />}
                        />
                    </SubForm>
                ))}
                {(plan.pbm || []).length === 0 && <p>No PBM yet.</p>}
            </SubForm>
            <SubForm title="Utilization management">
                <div className="internal-work__fields">
                    <Field label="Vendor" name={`${name}.utilizationManagement.vendorName`} />
                    <Field label="Notes" value={(plan.utilizationManagement?.notes || []).join(" · ")} />
                </div>
            </SubForm>
            <SubForm
                title="Services"
                onAdd={() => setServiceDialog({ key: "" })}
            >
            {serviceDialog ? (
                <PlanServiceDialog
                    plan={plan}
                    serviceKey={serviceDialog.key}
                    editPlan={editPlan}
                    onClose={() => setServiceDialog(null)}
                />
            ) : null}
            <div className="internal-work__service-tools">
                <input
                    type="search"
                    className="internal-work__search is-compact"
                    aria-label="Filter services by name"
                    placeholder="Filter services by name"
                    value={serviceQuery}
                    onChange={(event) => setServiceQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") event.preventDefault();
                        if (event.key === "Escape") setServiceQuery("");
                    }}
                />
                {detailKeys.length ? (
                    <button type="button" onClick={() => setAllDetails(!allDetailsOpen)}>
                        {allDetailsOpen ? "Hide all rules" : "Show all rules"}
                    </button>
                ) : null}
            </div>
            <div className="internal-work__sheet" hidden={!shownServiceRows.length}>
                <InternalTable
                    columns={["Service", "INN covered", "INN copay", "INN %", "INN ded", "OON covered", "OON copay", "OON %", "OON ded", "Limits", "Codes", "Notes", ""]}
                    rows={shownServiceRows.map((row) => {
                        if (row.kind === "adjudication") {
                            if (!openDetails.has(`${row.parentKind}-${row.parentKey}`)) return null;
                            return {
                                key: `adj-${row.parentKind}-${row.parentKey}`,
                                className: "is-adjudication",
                                span: true,
                                cells: [<AdjudicationPanel key={`adj-${row.parentKind}-${row.parentKey}`} row={row} editPlan={editPlan} onOpenForm={(key) => setServiceDialog({ key })} />],
                            };
                        }
                        const subRow = row.kind === "variant" || row.kind === "penalty" || row.kind === "visit" || row.kind === "precert" || row.kind === "maxDays";
                        const rowKey = row.kind === "variant"
                            ? `${row.parentKind}-${row.parentKey}-v${row.variantIndex}`
                            : row.kind === "penalty"
                                ? `penalty-${row.penaltyKey}-${row.parentKey}`
                                : row.kind === "visit"
                                    ? `visit-${row.field}-${row.parentKey}`
                                    : row.kind === "precert"
                                        ? `precert-${row.parentKey}`
                                        : row.kind === "maxDays"
                                            ? `max-days-${row.parentKey}`
                                            : `${row.kind}-${row.key}`;
                        const shown = row.kind === "variant" ? variantShown(row) : null;
                        const values = row.kind === "penalty"
                            ? [row.label, ...penaltyCells(row.inn), ...penaltyCells(row.oon), "", "", row.condition ? bothSides(row.inn?.condition || "", row.oon?.condition || "") : ""]
                            : row.kind === "maxDays"
                                ? [row.label, "", row.days == null ? "" : String(row.days), "", "", "", "", "", "", "", "", ""]
                                : row.kind === "visit"
                                ? [row.label, ...visitCells(row.inn), ...visitCells(row.oon), "", "", row.memberPcp ? renderedByLabel(row) : ""]
                                : row.kind === "precert"
                                    ? [row.label, ...precertCells(row.inn), ...precertCells(row.oon), "", "", bothSides(responsibilityLabel(row.inn), responsibilityLabel(row.oon))]
                                    : [
                                row.label,
                                ...sideCells(row.inn),
                                ...sideCells(row.oon),
                                row.kind === "variant" ? "" : cellText(bothSides((row.inn?.limits || []).map(limitText).filter(Boolean).join(" · "), (row.oon?.limits || []).map(limitText).filter(Boolean).join(" · "))),
                                row.kind === "variant" ? "" : cellText(bothSides(codeText(row.inn), codeText(row.oon))),
                                row.kind === "variant" ? "" : cellText(bothSides(noteText(row.inn), noteText(row.oon))),
                            ];
                        return {
                            key: rowKey,
                            className: subRow ? "is-variant" : "is-service-start",
                            cells: [
                                <div className="internal-work__service-line" key={`${rowKey}-name`}>
                                    {row.kind === "penalty" || row.kind === "visit" || row.kind === "precert" || row.kind === "maxDays" ? (
                                        <span className="internal-work__cell">{row.label}</span>
                                    ) : row.kind === "variant" ? (
                                        <EditableCell
                                            kind="variant"
                                            value={variantIdentity(row.inn, row.oon)}
                                            placesOfService={shown?.placesOfService || []}
                                            modifiers={shown?.modifiers || []}
                                            onCommit={(next) => editPlan((target) => commitServiceCell(target, row, 0, next))}
                                        />
                                    ) : (
                                        <>
                                            <EditableCell
                                                value={values[0]}
                                                kind="service"
                                                serviceKey={row.kind === "service" ? row.key : ""}
                                                usedKeys={serviceKeys(plan)}
                                                onCommit={(next) => editPlan((target) => commitServiceCell(target, row, 0, next))}
                                            />
                                            <IconButton
                                                label={`Add place of service on ${row.label}`}
                                                onClick={() => editPlan((target) => addServiceVariant(target, row))}
                                            >
                                                <LuPlus />
                                            </IconButton>
                                            <button
                                                type="button"
                                                className="internal-work__details-toggle"
                                                aria-expanded={openDetails.has(`${row.kind}-${row.key}`)}
                                                aria-label={`${openDetails.has(`${row.kind}-${row.key}`) ? "Hide" : "Show"} rules for ${row.label}`}
                                                onClick={() => toggleDetails(`${row.kind}-${row.key}`)}
                                            >
                                                Rules
                                                <LuChevronDown aria-hidden="true" />
                                            </button>
                                        </>
                                    )}
                                </div>,
                                ...values.slice(1).map((value, index) => {
                                    const column = index + 1;
                                    const penaltyLocked = row.kind === "penalty" && (
                                        column % 4 === 0 || column % 4 === 1 || (column >= 9 && !(row.condition && column === 11))
                                    );
                                    const daysLocked = row.kind === "maxDays" && column !== 2;
                                    const visitLocked = row.kind === "visit" && (column === 9 || column === 10 || (column === 11 && !row.memberPcp));
                                    const precertLocked = row.kind === "precert" && (column === 9 || column === 10 || column % 4 === 0);
                                    if ((row.kind === "variant" && column >= 9) || penaltyLocked || visitLocked || precertLocked || daysLocked) {
                                        return <span key={`${rowKey}-${column}`} className="internal-work__cell is-locked" />;
                                    }
                                    const memberPcp = row.kind === "visit" && row.memberPcp && column === 11;
                                    const whoPays = row.kind === "precert" && column === 11;
                                    const shareLimit = column === 9 && row.kind === "service" && ["outpatientRehabilitation", "chiropractice", "acupuncture"].includes(row.key);
                                    return (
                                        <EditableCell
                                            key={`${rowKey}-${column}`}
                                            value={value}
                                            kind={whoPays ? "choice" : (memberPcp || (column < 9 && column % 4 !== 2 && column % 4 !== 3)) ? "bool" : "text"}
                                            placeholder={
                                                memberPcp ? "Member's PCP"
                                                    : whoPays ? "Who pays"
                                                        : row.kind === "penalty" && row.condition && column === 11 ? "When it does not apply"
                                                            : row.kind === "maxDays" && column === 2 ? "Days"
                                                                : shareLimit ? "30 days / year shared with Chiropractice"
                                                                    : undefined
                                            }
                                            choices={whoPays ? [{ value: "Provider", label: "Provider" }, { value: "Member", label: "Member" }] : undefined}
                                            onCommit={(next) => editPlan((target) => commitServiceCell(target, row, column, next))}
                                        />
                                    );
                                }),
                                <IconButton
                                    key={`${rowKey}-remove`}
                                    label={`Remove ${row.label}`}
                                    danger
                                    onClick={() => editPlan((target) => {
                                        if (row.kind === "maxDays") {
                                            delete target.inNetwork?.[row.parentKey]?.maxNumberOfDaysCovered;
                                            delete target.outOfNetwork?.[row.parentKey]?.maxNumberOfDaysCovered;
                                            return;
                                        }
                                        if (row.kind === "penalty") {
                                            delete target.inNetwork?.[row.parentKey]?.[row.penaltyKey];
                                            delete target.outOfNetwork?.[row.parentKey]?.[row.penaltyKey];
                                            return;
                                        }
                                        if (row.kind === "visit") {
                                            delete target.inNetwork?.[row.parentKey]?.[row.field];
                                            delete target.outOfNetwork?.[row.parentKey]?.[row.field];
                                            return;
                                        }
                                        if (row.kind === "precert") {
                                            delete target.inNetwork?.[row.parentKey]?.precertification;
                                            delete target.outOfNetwork?.[row.parentKey]?.precertification;
                                            return;
                                        }
                                        if (row.kind === "variant") {
                                            removeServiceVariant(target, row);
                                            return;
                                        }
                                        if (row.kind === "service") {
                                            delete target.inNetwork?.[row.key];
                                            delete target.outOfNetwork?.[row.key];
                                        } else if (row.kind === "excluded") {
                                            delete target.inNetwork?.excludedServices?.[row.key];
                                            delete target.outOfNetwork?.excludedServices?.[row.key];
                                        } else {
                                            target.coverage.splice(row.index, 1);
                                        }
                                    })}
                                >
                                    <LuTrash2 />
                                </IconButton>,
                            ],
                        };
                    }).filter(Boolean)}
                />
            </div>
            {!shownServiceRows.length ? (
                <div className="internal-work__empty" role="status">
                    <LuSearchX aria-hidden="true" />
                    <div>
                        <b>{serviceQuery.trim() ? `No services match "${serviceQuery.trim()}"` : "No services on this plan yet"}</b>
                        <p>{serviceQuery.trim() ? "Check the spelling or try part of the name, like \"visit\" or \"lab\"." : "Use the + next to Services to add one from the catalog."}</p>
                    </div>
                    {serviceQuery.trim() ? (
                        <button type="button" onClick={() => setServiceQuery("")}>Clear filter</button>
                    ) : null}
                </div>
            ) : null}
            </SubForm>
            <SubForm title="Rx coverages">
                <RxForm plan={plan} name={name} editPlan={editPlan} />
            </SubForm>
            <SubForm
                title="Rules"
                onAdd={() => editPlan((target) => {
                    target.rules = [...(target.rules || []), { name: "", network: "", text: "" }];
                })}
            >
                {(plan.rules || []).map((rule, index) => (
                    <div className="internal-work__item is-rule" key={`${rule.name}-${index}`}>
                        <div className="internal-work__fields is-inline">
                            <Field label="Rule" name={`${name}.rules.${index}.name`} />
                            <Field label="Network" name={`${name}.rules.${index}.network`} />
                            <Field label="Text" name={`${name}.rules.${index}.text`} />
                        </div>
                        <IconButton
                            label={`Remove ${rule.name || "rule"}`}
                            danger
                            onClick={() => editPlan((target) => {
                                target.rules.splice(index, 1);
                            })}
                        >
                            <LuTrash2 />
                        </IconButton>
                    </div>
                ))}
                {(plan.rules || []).length === 0 && <p>No rules yet.</p>}
            </SubForm>
            <SubForm title="Notes" onAdd={() => editPlan((target) => { target.notes = [...(target.notes || []), emptyNote()]; })}>
                <Notes name={`${name}.notes`} />
            </SubForm>
        </article>
    );
};

const PlansForm = ({ account, edit }) => {
    const plans = account.benefitPlans || [];
    const [planIndex, setPlanIndex] = useState(0);
    const index = plans[planIndex] ? planIndex : 0;
    const plan = plans[index];
    const editPlan = (recipe) => edit((next) => {
        const target = next.benefitPlans?.[index];
        if (target) recipe(target);
    });
    const addPlan = () => {
        const used = new Set(plans.map((item) => item.planName));
        let planName = "New plan";
        let count = 2;
        while (used.has(planName)) {
            planName = `New plan ${count}`;
            count += 1;
        }
        edit((next) => {
            next.benefitPlans = [...(next.benefitPlans || []), emptyPlan(planName)];
        });
        setPlanIndex(plans.length);
    };
    return (
        <FormSection
            title="Plans"
            description="Plan name, plan ID, census tiers, accumulators, networks, and covered services. Add a place of service when that situation pays differently."
        >
            <div className="internal-work__tabs" role="tablist" aria-label="Benefit plans">
                {plans.map((item, itemIndex) => (
                    <button
                        key={`${item.planId || item.planName}-${itemIndex}`}
                        type="button"
                        role="tab"
                        aria-selected={itemIndex === index}
                        className={itemIndex === index ? "is-on" : undefined}
                        onClick={() => setPlanIndex(itemIndex)}
                    >
                        {item.planName}
                    </button>
                ))}
                <IconButton label="Add plan" onClick={addPlan}><LuPlus /></IconButton>
            </div>
            {plan ? <PlanForm plan={plan} name={`benefitPlans.${index}`} editPlan={editPlan} /> : <p>No plans yet.</p>}
        </FormSection>
    );
};

const syncLasers = (leaser) => {
    const lasers = leaser.lasers || [];
    leaser.claimants = lasers.length;
    leaser.totlaLaserLiability = lasers.reduce((sum, laser) => sum + (Number(laser.amount) || 0), 0);
};

const StopLossForm = ({ account, edit }) => {
    const { setValue } = useFormContext();
    const stop = account.stoploss;
    const setLaser = (index, field, current, next, numeric) => {
        const value = numeric ? (String(next).trim() === "" ? null : Number(next)) : next;
        if (numeric && value !== null && Number.isNaN(value)) return;
        if (value === current) return;
        setValue(`stoploss.specific.leaser.lasers.${index}.${field}`, value, { shouldDirty: true });
    };
    if (!stop) {
        return (
            <FormSection title="Stop-loss" description="Carrier, contract, specific, laser, aggregate, and premium by census tier.">
                <p>No stop-loss yet.</p>
            </FormSection>
        );
    }
    return (
        <FormSection title="Stop-loss" description="Carrier, contract, specific, laser, aggregate, and premium by census tier.">
            <div className="internal-work__fields">
                <Field label="Carrier" name="stoploss.carrier" />
                <Field label="Contract" name="stoploss.contractType" />
                <div className="internal-work__dates">
                    <DateField label="Effective date" name="stoploss.effectiveDate" />
                    <DateField label="Termination date" name="stoploss.terminationDate" />
                </div>
            </div>
            <SubForm title="Specific">
                <div className="internal-work__fields">
                    <Field label="Threshold" name="stoploss.specific.threshold" numeric />
                    <Field label="Lifetime maximum" name="stoploss.specific.lifetimeMaximumBenefit" numeric />
                </div>
            </SubForm>
            <SubForm title="Aggregate">
                <div className="internal-work__fields">
                    <Field label="Expected annual claims" name="stoploss.aggregated.expectedAnnualClaims" numeric />
                    <Field label="Corridor" name="stoploss.aggregated.corridorPercentage" numeric />
                    <Field label="Attachment point" name="stoploss.aggregated.attachmentPoint" numeric wide />
                </div>
            </SubForm>
            <SubForm
                title="Laser"
                onAdd={() => edit((next) => {
                    const leaser = next.stoploss.specific.leaser;
                    leaser.lasers = [...(leaser.lasers || []), { claimant: "", amount: null, notes: "" }];
                    syncLasers(leaser);
                })}
            >
                <div className="internal-work__fields">
                    <Field label="Claimants" value={stop.specific?.leaser?.claimants} />
                    <Field label="Total laser liability" value={money(stop.specific?.leaser?.totlaLaserLiability)} />
                </div>
                <div className="internal-work__sheet">
                <InternalTable
                    columns={["Claimant", "Lasered specific", "Notes", ""]}
                    rows={(stop.specific?.leaser?.lasers || []).map((laser, index) => ({
                        key: `${laser.claimant}-${index}`,
                        cells: [
                            <EditableCell
                                key="claimant"
                                value={laser.claimant || ""}
                                onCommit={(next) => setLaser(index, "claimant", laser.claimant || "", next)}
                            />,
                            <EditableCell
                                key="amount"
                                value={laser.amount == null ? "" : String(laser.amount)}
                                onCommit={(next) => setLaser(index, "amount", laser.amount ?? null, next, true)}
                            />,
                            <EditableCell
                                key="notes"
                                value={laser.notes || ""}
                                onCommit={(next) => setLaser(index, "notes", laser.notes || "", next)}
                            />,
                            <IconButton
                                label={`Remove ${laser.claimant || "laser"}`}
                                danger
                                onClick={() => edit((next) => {
                                    const leaser = next.stoploss.specific.leaser;
                                    leaser.lasers.splice(index, 1);
                                    syncLasers(leaser);
                                })}
                            >
                                <LuTrash2 />
                            </IconButton>,
                        ],
                    }))}
                />
                </div>
            </SubForm>
            <SubForm title="Premium">
            <InternalTable
                columns={["Census tier", "Code", "Premium"]}
                rows={(stop.premiumRatesByTier || []).map((tier) => ({
                    key: tier.code || tier.tierName,
                    cells: [tier.tierName, tier.code, money(tier.rate)],
                }))}
            />
            </SubForm>
            <SubForm title="Notes" onAdd={() => edit((next) => { next.stoploss.notes = [...(next.stoploss.notes || []), emptyNote()]; })}>
                <Notes name="stoploss.notes" />
            </SubForm>
        </FormSection>
    );
};

// Inputs register missing fields as "" or undefined, and numeric text comes back as strings.
// None of that is a change the user made, so compare a normalized copy.
const comparable = (value) => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return Number(value);
    if (Array.isArray(value)) {
        const items = value.map((item) => comparable(item) ?? null);
        return items.length ? items : undefined;
    }
    if (typeof value === "object") {
        const entries = Object.keys(value)
            .sort()
            .map((key) => [key, comparable(value[key])])
            .filter(([, item]) => item !== undefined);
        return entries.length ? Object.fromEntries(entries) : undefined;
    }
    return value;
};
const snapshot = (value) => JSON.stringify(comparable(value) ?? {});

const AccountHome = () => {
    const { desk } = useOutletContext();
    const location = useLocation();
    const { accountId } = useParams();
    const stored = findAccount(accountId);
    const methods = useForm({ defaultValues: stored || {} });
    const account = useWatch({ control: methods.control }) || methods.getValues();
    const [original, setOriginal] = useState(() => (stored ? structuredClone(stored) : null));
    const [baseline, setBaseline] = useState(() => snapshot(stored));
    const isDirty = snapshot(account) !== baseline;
    const blocker = useBlocker(({ currentLocation, nextLocation }) => (
        isDirty && currentLocation.pathname !== nextLocation.pathname
    ));
    useEffect(() => {
        if (!isDirty) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [isDirty]);
    const [sidebar, setSidebar] = useState("json");
    const [emrTab, setEmrTab] = useState("claim");
    const [diffSplit, setDiffSplit] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(420);
    const splitRef = useRef(null);
    const edit = (recipe) => {
        const next = structuredClone(methods.getValues());
        recipe(next);
        methods.reset(next, { keepDefaultValues: true });
    };
    const onSubmit = (values) => {
        console.log(values);
        setOriginal(structuredClone(values));
        setBaseline(snapshot(values));
    };
    const onReset = () => {
        methods.reset(structuredClone(original));
    };
    const [fold, setFold] = useState({ open: true, tick: 0 });
    const [canEdit, setCanEdit] = useState(() => Boolean(location.state?.edit));
    const setAll = (open) => setFold((current) => ({ open, tick: current.tick + 1 }));
    const resizeSidebar = (event) => {
        event.preventDefault();
        const handle = event.currentTarget;
        try {
            handle.setPointerCapture(event.pointerId);
        } catch {
            // The pointer is already gone.
        }
        const move = (moveEvent) => {
            const bounds = splitRef.current?.getBoundingClientRect();
            if (!bounds) return;
            const next = bounds.right - moveEvent.clientX;
            const min = 280;
            const max = Math.max(min, bounds.width - 280);
            setSidebarWidth(Math.round(Math.min(max, Math.max(min, next))));
        };
        const stop = () => {
            handle.removeEventListener("pointermove", move);
            handle.removeEventListener("pointerup", stop);
        };
        handle.addEventListener("pointermove", move);
        handle.addEventListener("pointerup", stop);
    };
    if (!stored) {
        return (
            <>
                <Link className="internal-work__back" to={`/internal/${desk.id}`}>Accounts</Link>
                <h1>That account is not on the list</h1>
            </>
        );
    }

    const form = (
        <EditModeContext.Provider value={canEdit}>
        <FoldContext.Provider value={fold}>
            <form className={canEdit ? undefined : "is-readonly"} onSubmit={methods.handleSubmit(onSubmit)}>
            <Link className="internal-work__back" to={`/internal/${desk.id}`}>Accounts</Link>
            <div className="internal-work__heading">
                <div>
                    <p className="internal-work__eyebrow">{desk.label}</p>
                    <div className="internal-work__name">
                        <h1>{account.accountName || "New account"}</h1>
                        <div className="internal-work__fold">
                            <button type="button" aria-label="Expand all" onClick={() => setAll(true)}><LuChevronsDown /></button>
                            <button type="button" aria-label="Collapse all" onClick={() => setAll(false)}><LuChevronsUp /></button>
                        </div>
                    </div>
                </div>
                <div className="internal-work__heading-actions">
                    <div className="internal-work__mode" role="group" aria-label="Form mode">
                        <button
                            type="button"
                            aria-pressed={!canEdit}
                            aria-disabled={canEdit && isDirty}
                            className={canEdit ? undefined : "is-read"}
                            title={canEdit && isDirty ? "Reset changes before switching to read only" : undefined}
                            onClick={() => {
                                if (!isDirty) setCanEdit(false);
                            }}
                        >Read only</button>
                        <button type="button" aria-pressed={canEdit} className={canEdit ? "is-edit" : undefined} onClick={() => setCanEdit(true)}>Edit</button>
                    </div>
                    {canEdit && (
                        <div className="internal-work__form-actions">
                            <button type="button" className="internal-work__reset" disabled={!isDirty} onClick={onReset}>Reset</button>
                            <button
                                type="submit"
                                className="internal-work__submit"
                                disabled={!isDirty}
                                title={isDirty ? undefined : "Make a change to submit"}
                            >Submit</button>
                        </div>
                    )}
                </div>
            </div>
            <div className="internal-work__chunks">
                <AccountForm account={account} edit={edit} />
                <StopLossForm account={account} edit={edit} />
                <PlansForm account={account} edit={edit} />
            </div>
            </form>
        </FoldContext.Provider>
        </EditModeContext.Provider>
    );

    if (desk.id !== "admin") {
        return (
            <FormProvider {...methods}>
                {form}
                <LeaveConfirm blocker={blocker} />
            </FormProvider>
        );
    }

    return (
        <FormProvider {...methods}>
        <div
            ref={splitRef}
            className="internal-work__split"
            style={{ "--sidebar-width": `${sidebarWidth}px` }}
        >
            <div className="internal-work__main">{form}</div>
            <LeaveConfirm blocker={blocker} />
            <div
                className="internal-work__json-drag is-grip"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize sidebar"
                title="Drag to resize"
                onPointerDown={resizeSidebar}
            />
            <aside className="internal-work__json" aria-label="Account JSON">
                <div className="internal-work__json-bar">
                    <div className="internal-work__mode" role="group" aria-label="Mock EMR">
                        <button
                            type="button"
                            aria-pressed={sidebar === "claims"}
                            className={sidebar === "claims" ? "is-on" : undefined}
                            onClick={() => {
                                setSidebar("claims");
                                setSidebarWidth((width) => Math.max(width, 560));
                            }}
                        >EMR</button>
                    </div>
                    <div className="internal-work__json-views">
                        {sidebar === "diff" && (
                            <div className="internal-work__mode" role="group" aria-label="Diff layout">
                                <button type="button" aria-pressed={!diffSplit} className={diffSplit ? undefined : "is-on"} onClick={() => setDiffSplit(false)}>Inline</button>
                                <button type="button" aria-pressed={diffSplit} className={diffSplit ? "is-on" : undefined} onClick={() => setDiffSplit(true)}>Side by side</button>
                            </div>
                        )}
                        <div className="internal-work__mode" role="group" aria-label="Sidebar view">
                            <button type="button" aria-pressed={sidebar === "json"} className={sidebar === "json" ? "is-on" : undefined} onClick={() => setSidebar("json")}>JSON</button>
                            <button type="button" aria-pressed={sidebar === "diff"} className={sidebar === "diff" ? "is-on" : undefined} onClick={() => setSidebar("diff")}>Diff</button>
                        </div>
                    </div>
                    {sidebar === "claims" ? <EmrTabs tab={emrTab} onChange={setEmrTab} /> : null}
                </div>
                {sidebar === "claims" ? (
                    <EmrPanel account={account} tab={emrTab} />
                ) : sidebar === "json" ? (
                    <ReactJson
                        src={account}
                        name={false}
                        collapsed={1}
                        collapseStringsAfterLength={80}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={false}
                    />
                ) : (
                    <AccountDiff original={original} current={account} splitView={diffSplit} />
                )}
            </aside>
        </div>
        </FormProvider>
    );
};

export default AccountHome;

const AccountDiff = ({ original, current, splitView }) => {
    const before = JSON.stringify(original, null, 2);
    const after = JSON.stringify(current, null, 2);
    if (before === after) return <p>No changes yet.</p>;
    return (
        <div className="internal-work__diff">
            <ReactDiffViewer
                oldValue={before}
                newValue={after}
                splitView={splitView}
                leftTitle={splitView ? "Opened" : undefined}
                rightTitle={splitView ? "Current" : undefined}
                disableWordDiff
            />
        </div>
    );
};
